/**
 * Elite Batch 21 Part 1 — Security & Compliance (17) + Support S1 Conversational Bots (9)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH21_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─────────────────────────────────────────────────────────
  // 1. Legal Hold Notification System
  // ─────────────────────────────────────────────────────────
  "Legal Hold Notification System": {
    nodes: [
      trigger("Legal hold event received", "webhook_app", {
        source: "legal-hold-system",
        event: "hold.initiated",
        howItStarts:
          "Fires when a legal hold event is triggered from the legal case management system (e.g., Relativity, Exterro, or custom system).",
        setup:
          "Configure your legal hold platform to POST to this webhook URL when a new hold is initiated or updated.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract matter ID, custodians, and summary from payload",
        280,
        "You are a legal hold data extractor. Parse the incoming webhook payload and extract all relevant litigation hold details regardless of field naming conventions.",
        "Extract from this legal hold event payload: {webhook_payload}. Return JSON with: matter_id (string), matter_name (string), custodian_emails (array of email strings), custodian_names (array of name strings), matter_summary (string describing the legal matter), priority_level (high/medium/low), hold_type (litigation/regulatory/investigation), requesting_attorney (string).",
        [
          { field: "matter_id", as: "matter_id", type: "string" },
          { field: "matter_name", as: "matter_name", type: "string" },
          { field: "custodian_emails", as: "custodian_emails", type: "string" },
          { field: "custodian_names", as: "custodian_names", type: "string" },
          { field: "matter_summary", as: "matter_summary", type: "string" },
          { field: "priority_level", as: "priority_level", type: "string" },
          { field: "hold_type", as: "hold_type", type: "string" },
          {
            field: "requesting_attorney",
            as: "requesting_attorney",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Look up custodian details via Microsoft Graph", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.microsoft.com/v1.0/users?$filter=mail in ({custodian_emails})&$select=displayName,mail,department,jobTitle,manager",
          headers: [
            { key: "Authorization", value: "Bearer {ms_graph_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "value", customName: "custodian_profiles", type: "string" },
            {
              name: "value.length",
              customName: "custodian_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "Analyze hold scope and generate preservation instructions",
        760,
        "You are a legal hold specialist. Analyze the matter details and custodian profiles to generate specific, actionable preservation instructions tailored to each custodian's role and data access patterns.",
        "Matter: {matter_name} ({matter_id}). Summary: {matter_summary}. Hold type: {hold_type}. Custodians: {custodian_profiles}. Generate JSON with: preservation_instructions (detailed string with numbered steps), data_sources_to_preserve (array of strings like email, Slack, OneDrive, etc.), estimated_scope (string), high_priority_custodians (array of emails for executives or key persons), acknowledgment_deadline (string like 48 hours or 72 hours).",
        [
          {
            field: "preservation_instructions",
            as: "preservation_instructions",
            type: "string",
          },
          {
            field: "data_sources_to_preserve",
            as: "data_sources_to_preserve",
            type: "string",
          },
          { field: "estimated_scope", as: "estimated_scope", type: "string" },
          {
            field: "high_priority_custodians",
            as: "high_priority_custodians",
            type: "string",
          },
          {
            field: "acknowledgment_deadline",
            as: "acknowledgment_deadline",
            type: "string",
          },
        ],
      ),
      n(
        "5",
        "condition",
        "Are there high-priority custodians (executives/key persons)?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "high_priority_custodians",
                operator: "not_equals",
                value: "[]",
              },
            ],
          },
        },
      ),
      n(
        "6",
        "approvalGate",
        "Legal reviewer approves preservation instructions",
        1240,
        {
          approvalDetails: {
            approver: "legal-review-team",
            timeout: 24,
            timeoutUnit: "hours",
            message:
              "Review and approve preservation instructions for matter {matter_id}: {matter_name}. Instructions: {preservation_instructions}. High-priority custodians identified: {high_priority_custodians}.",
          },
        },
      ),
      n(
        "7",
        "hitApi",
        "Send hold notices via SendGrid with acknowledgment links",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.sendgrid.com/v3/mail/send",
            headers: [
              { key: "Authorization", value: "Bearer {sendgrid_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "personalizations",
                value:
                  '[{"to": [{custodian_emails}], "dynamic_template_data": {"matter_name": "{matter_name}", "matter_id": "{matter_id}", "instructions": "{preservation_instructions}", "data_sources": "{data_sources_to_preserve}", "deadline": "{acknowledgment_deadline}", "attorney": "{requesting_attorney}"}}]',
              },
              {
                key: "from",
                value:
                  '{"email": "legal-holds@company.com", "name": "Legal Hold System"}',
              },
              { key: "template_id", value: "d-legal-hold-notice-template" },
            ],
            variables: [
              {
                name: "statusCode",
                customName: "email_status",
                type: "number",
              },
            ],
          },
        },
      ),
      slackNode(
        "8",
        "Alert legal team about non-responses after deadline",
        1720,
        ":rotating_light: *Legal Hold Non-Response Alert*\nMatter: {matter_name} ({matter_id})\nHold Type: {hold_type}\nCustodians notified: {custodian_count}\nHigh-priority custodians: {high_priority_custodians}\nAcknowledgment deadline: {acknowledgment_deadline}\nPreservation scope: {estimated_scope}\nRequesting Attorney: {requesting_attorney}\n\nPlease follow up with non-responding custodians immediately.",
      ),
      sheetsNode(
        "9",
        "Log compliance audit trail to Google Sheets",
        1960,
        "LegalHoldAudit",
        '[["{matter_id}", "{matter_name}", "{hold_type}", "{custodian_count}", "{priority_level}", "{data_sources_to_preserve}", "{acknowledgment_deadline}", "{requesting_attorney}", "=NOW()", "notices_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "Automates legal hold lifecycle from event detection through custodian lookup, AI-generated preservation instructions, legal review approval, SendGrid notice dispatch with acknowledgment tracking, Slack escalation for non-responses, and full audit trail logging.",
  },

  // ─────────────────────────────────────────────────────────
  // 2. Nonprofit Compliance Calendar
  // ─────────────────────────────────────────────────────────
  "Nonprofit Compliance Calendar": {
    nodes: [
      trigger("Daily compliance deadline check at 7 AM", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs every morning at 7 AM to scan the master compliance obligations spreadsheet for upcoming deadlines across all jurisdictions.",
        setup:
          "Ensure Google Sheets contains a master compliance calendar with columns: obligation, jurisdiction, deadline, responsible_party, status, required_documents.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Fetch master compliance obligations from Google Sheets",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ComplianceCalendar!A2:H200",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "values",
                customName: "obligations_data",
                type: "string",
              },
              {
                name: "values.length",
                customName: "total_obligations",
                type: "number",
              },
            ],
          },
        },
      ),
      n("3", "runCode", "Compute days-to-deadline and warning windows", 520, {
        runCodeDetails: {
          code: `const today = new Date(); const obligations = JSON.parse('{obligations_data}'); const upcoming = obligations.filter(row => { const deadline = new Date(row[2]); const daysLeft = Math.ceil((deadline - today) / 86400000); return daysLeft >= 0 && daysLeft <= 14; }).map(row => ({ obligation: row[0], jurisdiction: row[1], deadline: row[2], responsible: row[3], status: row[4], docs: row[5], daysLeft: Math.ceil((new Date(row[2]) - today) / 86400000), urgency: Math.ceil((new Date(row[2]) - today) / 86400000) <= 3 ? 'critical' : Math.ceil((new Date(row[2]) - today) / 86400000) <= 7 ? 'high' : 'medium' })); return { upcoming_deadlines: JSON.stringify(upcoming), deadline_count: upcoming.length, critical_count: upcoming.filter(u => u.urgency === 'critical').length, has_upcoming: upcoming.length > 0 };`,
          outputs: [
            { name: "upcoming_deadlines", type: "string" },
            { name: "deadline_count", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "has_upcoming", type: "boolean" },
          ],
        },
      }),
      n("4", "condition", "Any deadlines within 14 days?", 760, {
        conditionDetails: {
          conditions: [
            { field: "has_upcoming", operator: "equals", value: "true" },
          ],
        },
      }),
      aiNode(
        "5",
        "Generate filing requirement summaries per jurisdiction",
        1000,
        "You are a nonprofit compliance specialist. Generate clear, actionable filing summaries for each upcoming deadline, including specific document requirements, filing methods, and fee information.",
        "Upcoming compliance deadlines: {upcoming_deadlines}. For each obligation, generate JSON with: summaries (array of objects with obligation, jurisdiction, deadline, daysLeft, urgency, filing_summary describing what must be filed and how, missing_docs listing any documents not yet confirmed, estimated_time for completion).",
        [{ field: "summaries", as: "filing_summaries", type: "string" }],
      ),
      emailNode(
        "6",
        "Email responsible parties with deadline details",
        1240,
        "{responsible_party_email}",
        "Action Required: {deadline_count} compliance deadlines approaching",
        "Hello,\n\nYou have {deadline_count} compliance obligations due within the next 14 days ({critical_count} are critical).\n\n{filing_summaries}\n\nPlease review each item and confirm document readiness. Critical items (3 days or fewer) require immediate attention.\n\nContact the compliance team if you need assistance with any filings.\n\nBest regards,\nCompliance Calendar System",
      ),
      slackNode(
        "7",
        "Post weekly compliance digest sorted by urgency",
        1480,
        ":calendar: *Nonprofit Compliance Weekly Digest*\n\nTotal upcoming deadlines (14-day window): {deadline_count}\nCritical (3 days or fewer): {critical_count}\n\n{filing_summaries}\n\nPlease ensure all responsible parties have confirmed document readiness. Escalate any items marked critical that lack assigned owners.",
      ),
      sheetsNode(
        "8",
        "Log completion status and escalations",
        1720,
        "ComplianceLog",
        '[["{deadline_count}", "{critical_count}", "{filing_summaries}", "=NOW()", "daily_scan"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily automated scan of nonprofit compliance obligations spreadsheet, computing deadline windows, generating jurisdiction-specific filing summaries with AI, emailing responsible parties, posting Slack digest sorted by urgency, and maintaining a compliance audit log.",
  },

  // ─────────────────────────────────────────────────────────
  // 3. PCI Compliance Checklist Bot
  // ─────────────────────────────────────────────────────────
  "PCI Compliance Checklist Bot": {
    nodes: [
      trigger("Merchant starts PCI assessment", "manual", {
        howItStarts:
          "An authorized user manually triggers the PCI DSS compliance assessment workflow for a merchant or business unit.",
        setup:
          "Ensure Pinecone index contains PCI DSS v4.0 policies, prior SAQ responses, and evidence documents. Configure merchant profile data.",
        inputFields: [
          { name: "merchant_name", type: "string", example: "Acme Payments" },
          { name: "merchant_level", type: "string", example: "Level 2" },
          { name: "saq_type", type: "string", example: "SAQ-D" },
          {
            name: "assessment_scope",
            type: "string",
            example: "Card-present and e-commerce",
          },
        ],
      }),
      aiNode(
        "2",
        "Classify assessment question to PCI DSS v4.0 requirement IDs",
        280,
        "You are a PCI DSS v4.0 qualified security assessor. Given a merchant profile, classify which PCI DSS requirements apply and assess confidence levels for each requirement area.",
        "Merchant: {merchant_name}, Level: {merchant_level}, SAQ type: {saq_type}, Scope: {assessment_scope}. Map to PCI DSS v4.0 requirements. Return JSON with: applicable_requirements (array of objects with req_id like 1.1.1, req_title, category like network_security/access_control/encryption, confidence 0-1, priority high/medium/low), total_requirements (number), high_priority_count (number).",
        [
          {
            field: "applicable_requirements",
            as: "applicable_requirements",
            type: "string",
          },
          {
            field: "total_requirements",
            as: "total_requirements",
            type: "number",
          },
          {
            field: "high_priority_count",
            as: "high_priority_count",
            type: "number",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Search Pinecone for relevant PCI policies and prior SAQs",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_host}/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{assessment_embedding}" },
              { key: "topK", value: "20" },
              { key: "namespace", value: "pci-policies" },
              { key: "includeMetadata", value: "true" },
              { key: "filter", value: '{"saq_type": {"$eq": "{saq_type}"}}' },
            ],
            variables: [
              { name: "matches", customName: "policy_matches", type: "string" },
              {
                name: "matches.length",
                customName: "policies_found",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "runCode",
        "Track per-requirement status and evidence freshness",
        760,
        {
          runCodeDetails: {
            code: `const requirements = JSON.parse('{applicable_requirements}'); const policies = JSON.parse('{policy_matches}'); const tracked = requirements.map(req => { const evidence = policies.filter(p => p.metadata && p.metadata.requirement_id === req.req_id); const freshest = evidence.length > 0 ? evidence.reduce((a, b) => new Date(a.metadata.updated) > new Date(b.metadata.updated) ? a : b) : null; const daysSinceUpdate = freshest ? Math.ceil((Date.now() - new Date(freshest.metadata.updated)) / 86400000) : 999; return { ...req, evidence_count: evidence.length, evidence_stale: daysSinceUpdate > 90, last_updated: freshest ? freshest.metadata.updated : 'never', status: evidence.length === 0 ? 'missing' : daysSinceUpdate > 90 ? 'stale' : 'current' }; }); const gaps = tracked.filter(t => t.status !== 'current'); return { tracked_requirements: JSON.stringify(tracked), gap_count: gaps.length, gaps_detail: JSON.stringify(gaps), all_current: gaps.length === 0 };`,
            outputs: [
              { name: "tracked_requirements", type: "string" },
              { name: "gap_count", type: "number" },
              { name: "gaps_detail", type: "string" },
              { name: "all_current", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Are there more than 3 unresolved compliance gaps?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { field: "gap_count", operator: "greater_than", value: "3" },
            ],
          },
        },
      ),
      emailNode(
        "6",
        "Email evidence collection requests with requirement guidance",
        1240,
        "{compliance_officer_email}",
        "PCI DSS Assessment: {gap_count} evidence gaps identified for {merchant_name}",
        "PCI DSS v4.0 Assessment Report for {merchant_name}\nSAQ Type: {saq_type} | Merchant Level: {merchant_level}\n\nTotal requirements assessed: {total_requirements}\nCompliance gaps identified: {gap_count}\n\nGaps requiring evidence collection:\n{gaps_detail}\n\nEach gap includes the specific requirement ID, description, and evidence needed. Please coordinate with the relevant teams to collect and upload evidence within 14 business days.\n\nFor stale evidence, please provide updated documentation confirming controls are still in place.\n\nCompliance Assessment System",
      ),
      slackNode(
        "7",
        "Route complex issues to QSA for review",
        1480,
        ":shield: *PCI DSS Assessment — QSA Review Needed*\nMerchant: {merchant_name} (Level {merchant_level})\nSAQ Type: {saq_type}\nTotal requirements: {total_requirements}\nUnresolved gaps: {gap_count}\nHigh priority items: {high_priority_count}\n\nGap details: {gaps_detail}\n\nPlease review these gaps and determine if compensating controls or additional testing is required.",
      ),
      sheetsNode(
        "8",
        "Compile compliance report with status and attestation prep",
        1720,
        "PCICompliance",
        '[["{merchant_name}", "{merchant_level}", "{saq_type}", "{total_requirements}", "{gap_count}", "{high_priority_count}", "{tracked_requirements}", "=NOW()", "assessment_in_progress"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "End-to-end PCI DSS v4.0 compliance assessment: classifies applicable requirements, searches prior policies and SAQ evidence in Pinecone, tracks per-requirement evidence freshness, emails gap reports for evidence collection, escalates complex gaps to QSA via Slack, and compiles audit-ready compliance reports.",
  },

  // ─────────────────────────────────────────────────────────
  // 4. Privacy Impact Assessment Workflow
  // ─────────────────────────────────────────────────────────
  "Privacy Impact Assessment Workflow": {
    nodes: [
      trigger("Project intake form submitted", "webhook_form", {
        source: "project-intake",
        event: "form.submitted",
        howItStarts:
          "Fires when a new project or system intake form is submitted, triggering a privacy impact assessment review.",
        setup:
          "Configure your project intake form to POST to this webhook URL when a new project is submitted for privacy review.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract system details, data types, legal basis, geography",
        280,
        "You are a privacy impact assessment intake processor. Extract all relevant project and data processing details from the submitted form, regardless of field naming conventions.",
        "Extract from this project intake submission: {webhook_payload}. Return JSON with: project_name (string), project_owner (string), project_owner_email (string), system_description (string), data_categories (array: personal, sensitive, financial, health, biometric, children, etc.), data_subjects (array: customers, employees, vendors, etc.), processing_purposes (array of strings), legal_basis (string: consent, contract, legitimate_interest, legal_obligation, vital_interest, public_task), geographies (array of country/region codes), third_party_processors (array of strings), data_retention_period (string), estimated_records (number).",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "project_owner", as: "project_owner", type: "string" },
          {
            field: "project_owner_email",
            as: "project_owner_email",
            type: "string",
          },
          {
            field: "system_description",
            as: "system_description",
            type: "string",
          },
          { field: "data_categories", as: "data_categories", type: "string" },
          { field: "data_subjects", as: "data_subjects", type: "string" },
          {
            field: "processing_purposes",
            as: "processing_purposes",
            type: "string",
          },
          { field: "legal_basis", as: "legal_basis", type: "string" },
          { field: "geographies", as: "geographies", type: "string" },
          {
            field: "third_party_processors",
            as: "third_party_processors",
            type: "string",
          },
          {
            field: "estimated_records",
            as: "estimated_records",
            type: "number",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Risk pre-screening: determine if full PIA needed and applicable regulations",
        520,
        {
          runCodeDetails: {
            code: `const categories = JSON.parse('{data_categories}'); const geos = JSON.parse('{geographies}'); const subjects = JSON.parse('{data_subjects}'); const records = {estimated_records}; let score = 0; const regulations = []; if (categories.includes('sensitive') || categories.includes('health') || categories.includes('biometric')) score += 30; if (categories.includes('children')) score += 40; if (categories.includes('financial')) score += 20; if (records > 10000) score += 15; if (records > 100000) score += 15; if (geos.some(g => ['EU','DE','FR','UK','GB'].includes(g))) { regulations.push('GDPR'); score += 10; } if (geos.some(g => ['US-CA'].includes(g))) { regulations.push('CCPA/CPRA'); score += 10; } if (geos.some(g => ['BR'].includes(g))) { regulations.push('LGPD'); score += 5; } if (categories.includes('health') && geos.some(g => g.startsWith('US'))) { regulations.push('HIPAA'); score += 15; } if (subjects.includes('children')) { regulations.push('COPPA'); score += 20; } const fullPiaNeeded = score > 40; return { risk_score: score, applicable_regulations: JSON.stringify(regulations), full_pia_needed: fullPiaNeeded, risk_level: score > 70 ? 'high' : score > 40 ? 'medium' : 'low' };`,
            outputs: [
              { name: "risk_score", type: "number" },
              { name: "applicable_regulations", type: "string" },
              { name: "full_pia_needed", type: "boolean" },
              { name: "risk_level", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "Generate tailored PIA questionnaire by project type",
        760,
        "You are a data protection officer. Generate a tailored privacy impact assessment questionnaire based on the project details, applicable regulations, and risk level. Focus questions on areas of highest risk.",
        "Project: {project_name}. Description: {system_description}. Data categories: {data_categories}. Subjects: {data_subjects}. Legal basis: {legal_basis}. Regulations: {applicable_regulations}. Risk score: {risk_score}. Risk level: {risk_level}. Processors: {third_party_processors}. Generate JSON with: questionnaire_sections (array of objects with section_name, questions array of strings, regulation_reference), total_questions (number), priority_areas (array of strings identifying highest risk areas), estimated_completion_time (string like 2-3 hours).",
        [
          {
            field: "questionnaire_sections",
            as: "questionnaire_sections",
            type: "string",
          },
          { field: "total_questions", as: "total_questions", type: "number" },
          { field: "priority_areas", as: "priority_areas", type: "string" },
          {
            field: "estimated_completion_time",
            as: "estimated_completion_time",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Is risk score above 70 (high risk)?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "risk_score", operator: "greater_than", value: "70" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email stakeholders with PIA questionnaire",
        1240,
        "{project_owner_email}",
        "Privacy Impact Assessment Required: {project_name} (Risk: {risk_level})",
        "Dear {project_owner},\n\nA Privacy Impact Assessment has been initiated for your project: {project_name}.\n\nRisk Level: {risk_level} (Score: {risk_score}/100)\nApplicable Regulations: {applicable_regulations}\nPriority Areas: {priority_areas}\nEstimated Completion Time: {estimated_completion_time}\n\nPlease complete the following questionnaire within 5 business days:\n\n{questionnaire_sections}\n\nTotal questions: {total_questions}\n\nThis assessment is required before the project can proceed to implementation. Please contact the privacy team with any questions.\n\nPrivacy Office",
      ),
      n("7", "approvalGate", "DPO review and sign-off on PIA", 1480, {
        approvalDetails: {
          approver: "dpo-team",
          timeout: 72,
          timeoutUnit: "hours",
          message:
            "Review Privacy Impact Assessment for {project_name}. Risk level: {risk_level} (score: {risk_score}). Applicable regulations: {applicable_regulations}. Priority areas: {priority_areas}. Data categories: {data_categories}. Geographies: {geographies}.",
        },
      }),
      aiNode(
        "8",
        "Generate mitigation recommendations based on PIA findings",
        1720,
        "You are a privacy risk mitigation specialist. Based on the completed PIA and identified risks, generate specific, actionable mitigation recommendations ordered by priority.",
        "Project: {project_name}. Risk level: {risk_level}. Score: {risk_score}. Regulations: {applicable_regulations}. Data categories: {data_categories}. Legal basis: {legal_basis}. Processors: {third_party_processors}. Priority areas: {priority_areas}. Generate JSON with: mitigations (array of objects with risk_area, recommendation, implementation_effort low/medium/high, regulatory_requirement boolean, deadline_suggestion), overall_recommendation (approve/approve_with_conditions/reject), conditions (array of strings if approve_with_conditions).",
        [
          { field: "mitigations", as: "mitigations", type: "string" },
          {
            field: "overall_recommendation",
            as: "overall_recommendation",
            type: "string",
          },
          { field: "conditions", as: "conditions", type: "string" },
        ],
      ),
      sheetsNode(
        "9",
        "Publish to PIA registry with version archive",
        1960,
        "PIARegistry",
        '[["{project_name}", "{project_owner}", "{risk_level}", "{risk_score}", "{applicable_regulations}", "{data_categories}", "{overall_recommendation}", "{conditions}", "{mitigations}", "=NOW()", "v1"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "Full privacy impact assessment lifecycle: intake form extraction, automated risk pre-screening against GDPR/CCPA/HIPAA/COPPA, tailored questionnaire generation, stakeholder notification, DPO approval gate, AI-generated mitigation recommendations, and PIA registry archival with versioning.",
  },

  // ─────────────────────────────────────────────────────────
  // 5. Privacy Policy Generator
  // ─────────────────────────────────────────────────────────
  "Privacy Policy Generator": {
    nodes: [
      trigger("Business profile form submitted", "webhook_form", {
        source: "privacy-policy-intake",
        event: "form.submitted",
        howItStarts:
          "Fires when a business completes the privacy policy intake form with details about their data practices, products, and jurisdictions.",
        setup:
          "Configure the business profile intake form to POST to this webhook URL upon submission.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract products, data categories, processors, jurisdictions",
        280,
        "You are a privacy policy data extractor. Parse the business profile submission and extract all details needed to generate a comprehensive privacy policy. Handle any form field naming convention.",
        "Extract from this business profile submission: {webhook_payload}. Return JSON with: company_name (string), company_email (string), legal_contact_email (string), website_url (string), products (array of product names/descriptions), data_categories_collected (array: name, email, IP, location, payment, cookies, etc.), collection_methods (array: forms, cookies, APIs, third_party, etc.), processing_purposes (array of strings), third_party_processors (array of objects with name and purpose), data_retention_days (number), jurisdictions (array of country/region codes like US, EU, CA, UK, BR), has_children_data (boolean), sells_data (boolean), automated_decision_making (boolean).",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_email", as: "company_email", type: "string" },
          {
            field: "legal_contact_email",
            as: "legal_contact_email",
            type: "string",
          },
          { field: "website_url", as: "website_url", type: "string" },
          { field: "products", as: "products", type: "string" },
          {
            field: "data_categories_collected",
            as: "data_categories_collected",
            type: "string",
          },
          {
            field: "processing_purposes",
            as: "processing_purposes",
            type: "string",
          },
          {
            field: "third_party_processors",
            as: "third_party_processors",
            type: "string",
          },
          { field: "jurisdictions", as: "jurisdictions", type: "string" },
          {
            field: "has_children_data",
            as: "has_children_data",
            type: "boolean",
          },
          { field: "sells_data", as: "sells_data", type: "boolean" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Determine required disclosures by jurisdiction matrix",
        520,
        {
          runCodeDetails: {
            code: `const jurisdictions = JSON.parse('{jurisdictions}'); const hasChildren = {has_children_data}; const sellsData = {sells_data}; const disclosures = []; const sections = ['data_collected', 'purposes', 'legal_basis', 'retention', 'third_parties', 'security_measures', 'contact']; if (jurisdictions.some(j => ['EU','DE','FR','IT','ES','NL','UK','GB'].includes(j))) { sections.push('gdpr_rights', 'dpo_contact', 'cross_border_transfers', 'legal_basis_detail'); disclosures.push('GDPR Article 13/14 disclosures required'); } if (jurisdictions.includes('US-CA') || jurisdictions.includes('CA')) { sections.push('ccpa_rights', 'do_not_sell', 'financial_incentives', 'shine_the_light'); disclosures.push('CCPA/CPRA disclosures required'); } if (jurisdictions.includes('BR')) { sections.push('lgpd_rights', 'dpo_brazil'); disclosures.push('LGPD disclosures required'); } if (hasChildren) { sections.push('children_privacy', 'parental_consent'); disclosures.push('COPPA compliance section required'); } if (sellsData) { sections.push('data_sales_disclosure', 'opt_out_instructions'); disclosures.push('Data sales disclosure required'); } return { required_sections: JSON.stringify([...new Set(sections)]), required_disclosures: JSON.stringify(disclosures), section_count: [...new Set(sections)].length };`,
            outputs: [
              { name: "required_sections", type: "string" },
              { name: "required_disclosures", type: "string" },
              { name: "section_count", type: "number" },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "Draft privacy policy sections from business facts",
        760,
        "You are an expert privacy attorney. Draft a complete, legally sound privacy policy based on the business facts provided. Use clear, plain language. Each section must be thorough and legally compliant for all applicable jurisdictions.",
        "Company: {company_name} ({website_url}). Products: {products}. Data collected: {data_categories_collected}. Purposes: {processing_purposes}. Processors: {third_party_processors}. Jurisdictions: {jurisdictions}. Required sections: {required_sections}. Required disclosures: {required_disclosures}. Children data: {has_children_data}. Sells data: {sells_data}. Draft JSON with: policy_sections (array of objects with section_title, section_content as formatted text, applicable_regulation), effective_date_suggestion (string), review_notes (array of items needing legal review).",
        [
          { field: "policy_sections", as: "policy_sections", type: "string" },
          {
            field: "effective_date_suggestion",
            as: "effective_date_suggestion",
            type: "string",
          },
          { field: "review_notes", as: "review_notes", type: "string" },
        ],
      ),
      n(
        "5",
        "runCode",
        "Validate clause coverage by jurisdiction requirements",
        760,
        {
          runCodeDetails: {
            code: `const sections = JSON.parse('{policy_sections}'); const required = JSON.parse('{required_sections}'); const sectionTitles = sections.map(s => s.section_title.toLowerCase().replace(/[^a-z_]/g, '_')); const missing = required.filter(r => !sectionTitles.some(t => t.includes(r.replace(/_/g, ' ')) || t.includes(r))); const coverage = ((required.length - missing.length) / required.length * 100).toFixed(1); return { missing_sections: JSON.stringify(missing), coverage_percent: parseFloat(coverage), is_complete: missing.length === 0, validation_status: missing.length === 0 ? 'pass' : 'gaps_found' };`,
            outputs: [
              { name: "missing_sections", type: "string" },
              { name: "coverage_percent", type: "number" },
              { name: "is_complete", type: "boolean" },
              { name: "validation_status", type: "string" },
            ],
          },
        },
        480,
      ),
      n("6", "approvalGate", "Legal team approves privacy policy draft", 1000, {
        approvalDetails: {
          approver: "legal-team",
          timeout: 48,
          timeoutUnit: "hours",
          message:
            "Review privacy policy draft for {company_name}. Coverage: {coverage_percent}%. Required disclosures: {required_disclosures}. Review notes from AI: {review_notes}. Missing sections: {missing_sections}. Validation: {validation_status}.",
        },
      }),
      n(
        "7",
        "hitApi",
        "Notify customers of material policy changes via SendGrid",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.sendgrid.com/v3/mail/send",
            headers: [
              { key: "Authorization", value: "Bearer {sendgrid_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "personalizations",
                value:
                  '[{"to": [{"email": "{customer_list}"}], "dynamic_template_data": {"company_name": "{company_name}", "effective_date": "{effective_date_suggestion}", "policy_url": "{website_url}/privacy"}}]',
              },
              {
                key: "from",
                value: '{"email": "{company_email}", "name": "{company_name}"}',
              },
              { key: "template_id", value: "d-privacy-policy-update-template" },
            ],
            variables: [
              {
                name: "statusCode",
                customName: "notification_status",
                type: "number",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Version control with effective date",
        1480,
        "PolicyVersions",
        '[["{company_name}", "{website_url}", "{jurisdictions}", "{section_count}", "{coverage_percent}", "{validation_status}", "{effective_date_suggestion}", "{review_notes}", "=NOW()", "published"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automated privacy policy generation: extracts business profile, determines jurisdiction-specific disclosure requirements (GDPR, CCPA, LGPD, COPPA), drafts legally-sound policy sections, validates clause coverage, routes through legal approval, notifies customers of changes via SendGrid, and archives versions.",
  },

  // ─────────────────────────────────────────────────────────
  // 6. SOC2 Evidence Collector
  // ─────────────────────────────────────────────────────────
  "SOC2 Evidence Collector": {
    nodes: [
      trigger("Weekly SOC2 evidence collection on Mondays", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to automatically collect evidence artifacts for SOC2 Trust Service Criteria from multiple systems.",
        setup:
          "Configure API keys for AWS Config, GitHub, Google Drive, and Qualys. Set up the evidence mapping spreadsheet.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Snapshot security-relevant configs from AWS Config",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://config.us-east-1.amazonaws.com/",
            headers: [
              { key: "Authorization", value: "{aws_sig_v4}" },
              { key: "Content-Type", value: "application/x-amz-json-1.1" },
              {
                key: "X-Amz-Target",
                value: "StarlingDoveService.GetComplianceDetailsByConfigRule",
              },
            ],
            body: [
              { key: "ConfigRuleName", value: "all" },
              {
                key: "ComplianceTypes",
                value: '["COMPLIANT","NON_COMPLIANT"]',
              },
              { key: "Limit", value: "100" },
            ],
            variables: [
              {
                name: "EvaluationResults",
                customName: "aws_config_results",
                type: "string",
              },
              {
                name: "EvaluationResults.length",
                customName: "aws_rules_evaluated",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "hitApi",
        "Collect access logs and branch rules from GitHub",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.github.com/orgs/{org}/audit-log?per_page=100&include=all&phrase=action:repo+created:>={last_week_date}",
            headers: [
              { key: "Authorization", value: "Bearer {github_token}" },
              { key: "Accept", value: "application/vnd.github+json" },
            ],
            body: [],
            variables: [
              { name: "root", customName: "github_audit_log", type: "string" },
              {
                name: "root.length",
                customName: "github_events_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "hitApi", "Fetch HR policy documents from Google Drive", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/drive/v3/files?q='HR_Policies_Folder_ID'+in+parents&fields=files(id,name,modifiedTime,owners)&orderBy=modifiedTime+desc",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "files", customName: "hr_policy_docs", type: "string" },
            {
              name: "files.length",
              customName: "hr_docs_count",
              type: "number",
            },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Pull vulnerability scan results from Qualys",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://qualysapi.qualys.com/api/2.0/fo/scan/?action=list",
            headers: [
              { key: "Authorization", value: "Basic {qualys_credentials}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [
              { key: "launched_after_datetime", value: "{last_week_date}" },
              { key: "scan_type", value: "Scheduled" },
            ],
            variables: [
              {
                name: "SCAN_LIST.SCAN",
                customName: "qualys_scans",
                type: "string",
              },
              {
                name: "SCAN_LIST.SCAN.length",
                customName: "scans_count",
                type: "number",
              },
            ],
          },
        },
        480,
      ),
      n(
        "6",
        "runCode",
        "Deduplicate artifacts and preserve provenance metadata",
        1240,
        {
          runCodeDetails: {
            code: `const awsResults = JSON.parse('{aws_config_results}'); const githubLog = JSON.parse('{github_audit_log}'); const hrDocs = JSON.parse('{hr_policy_docs}'); const qualys = '{qualys_scans}'; const artifacts = []; const seen = new Set(); awsResults.forEach(r => { const key = 'aws_' + r.EvaluationResultIdentifier?.EvaluationResultQualifier?.ConfigRuleName; if (!seen.has(key)) { seen.add(key); artifacts.push({ source: 'AWS Config', type: 'compliance_rule', id: key, collected: new Date().toISOString(), status: r.ComplianceType }); } }); const totalArtifacts = artifacts.length + {github_events_count} + {hr_docs_count} + {scans_count}; return { artifact_inventory: JSON.stringify(artifacts.slice(0, 50)), total_artifacts: totalArtifacts, sources_collected: 4, collection_timestamp: new Date().toISOString() };`,
            outputs: [
              { name: "artifact_inventory", type: "string" },
              { name: "total_artifacts", type: "number" },
              { name: "sources_collected", type: "number" },
              { name: "collection_timestamp", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "7",
        "Map artifacts to Trust Service Criteria and identify gaps",
        1480,
        "You are a SOC2 auditor assistant. Map the collected evidence artifacts to SOC2 Trust Service Criteria (Security, Availability, Processing Integrity, Confidentiality, Privacy). Identify any criteria with insufficient or missing evidence.",
        "Collected artifacts: {artifact_inventory}. Total: {total_artifacts} from {sources_collected} sources. AWS compliance rules: {aws_rules_evaluated}. GitHub audit events: {github_events_count}. HR policy docs: {hr_docs_count}. Vulnerability scans: {scans_count}. Map to TSC and return JSON with: criteria_mapping (array of objects with criterion_id like CC1.1, criterion_name, evidence_sources array, evidence_count, status sufficient/partial/missing), missing_evidence (array of objects with criterion_id and what_is_needed), overall_readiness_percent (number 0-100), gaps_count (number).",
        [
          { field: "criteria_mapping", as: "criteria_mapping", type: "string" },
          { field: "missing_evidence", as: "missing_evidence", type: "string" },
          {
            field: "overall_readiness_percent",
            as: "overall_readiness_percent",
            type: "number",
          },
          { field: "gaps_count", as: "gaps_count", type: "number" },
        ],
      ),
      n(
        "8",
        "condition",
        "Is there missing evidence for any TSC criterion?",
        1720,
        {
          conditionDetails: {
            conditions: [
              { field: "gaps_count", operator: "greater_than", value: "0" },
            ],
          },
        },
      ),
      slackNode(
        "9",
        "Alert control owners about stale or missing evidence",
        1960,
        ":warning: *SOC2 Evidence Collection Report — Gaps Found*\nCollection Date: {collection_timestamp}\nTotal artifacts collected: {total_artifacts}\nSources: {sources_collected}\nOverall readiness: {overall_readiness_percent}%\nGaps: {gaps_count}\n\nMissing evidence details:\n{missing_evidence}\n\nControl owners: please provide the missing evidence within 5 business days. Contact the compliance team for guidance.",
      ),
      sheetsNode(
        "10",
        "Compile auditor-ready evidence index",
        2200,
        "SOC2EvidenceIndex",
        '[["{collection_timestamp}", "{total_artifacts}", "{sources_collected}", "{aws_rules_evaluated}", "{github_events_count}", "{hr_docs_count}", "{scans_count}", "{overall_readiness_percent}", "{gaps_count}", "{missing_evidence}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "10", "output_2"),
      e("9", "10"),
    ],
    summary:
      "Weekly automated SOC2 evidence collection from AWS Config, GitHub audit logs, Google Drive HR policies, and Qualys vulnerability scans. Deduplicates artifacts, maps to Trust Service Criteria with AI, identifies evidence gaps, alerts control owners via Slack, and compiles an auditor-ready evidence index.",
  },

  // ─────────────────────────────────────────────────────────
  // 7. Secret Rotation Reminder
  // ─────────────────────────────────────────────────────────
  "Secret Rotation Reminder": {
    nodes: [
      trigger("Daily secret rotation check at 8 AM", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to enumerate all secrets in Vault and check rotation compliance against policy.",
        setup:
          "Configure Vault API access with read-only permissions. Define rotation policies by risk tier in the runCode node.",
        inputFields: [],
      }),
      n("2", "hitApi", "Enumerate secrets with metadata from Vault", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://vault.company.com/v1/secret/metadata?list=true",
          headers: [
            { key: "X-Vault-Token", value: "{vault_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data.keys", customName: "secret_keys", type: "string" },
            {
              name: "data.keys.length",
              customName: "total_secrets",
              type: "number",
            },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Compare secret age against rotation policy by risk tier",
        520,
        {
          runCodeDetails: {
            code: `const secrets = JSON.parse('{secret_keys}'); const policies = { critical: 30, high: 60, medium: 90, low: 180 }; const now = Date.now(); const results = secrets.map(s => { const tier = s.metadata?.risk_tier || 'medium'; const lastRotated = new Date(s.metadata?.last_rotated || '2024-01-01').getTime(); const ageDays = Math.ceil((now - lastRotated) / 86400000); const maxAge = policies[tier] || 90; const overdue = ageDays > maxAge; const daysOverdue = overdue ? ageDays - maxAge : 0; return { name: s.name || s, owner: s.metadata?.owner || 'unassigned', environment: s.metadata?.environment || 'unknown', tier, ageDays, maxAge, overdue, daysOverdue }; }); const overdueSecrets = results.filter(r => r.overdue); return { overdue_secrets: JSON.stringify(overdueSecrets), overdue_count: overdueSecrets.length, critical_overdue: overdueSecrets.filter(s => s.tier === 'critical').length, has_overdue: overdueSecrets.length > 0, total_checked: results.length };`,
            outputs: [
              { name: "overdue_secrets", type: "string" },
              { name: "overdue_count", type: "number" },
              { name: "critical_overdue", type: "number" },
              { name: "has_overdue", type: "boolean" },
              { name: "total_checked", type: "number" },
            ],
          },
        },
      ),
      n("4", "condition", "Any secrets overdue for rotation?", 760, {
        conditionDetails: {
          conditions: [
            { field: "has_overdue", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email secret owners with rotation instructions",
        1000,
        "{owner_emails}",
        "Action Required: {overdue_count} secrets overdue for rotation",
        "Hello,\n\nThe following secrets assigned to you are overdue for rotation per our security policy:\n\n{overdue_secrets}\n\nRotation Policy:\n- Critical tier: every 30 days\n- High tier: every 60 days\n- Medium tier: every 90 days\n- Low tier: every 180 days\n\nPlease rotate these secrets within 48 hours. Steps:\n1. Generate new secret value\n2. Update the secret in Vault\n3. Deploy configuration changes to affected services\n4. Verify service health after rotation\n\nIf you need assistance, contact the security team in #security-ops.\n\nSecurity Operations",
      ),
      n("6", "delayAction", "Wait 72 hours for rotation compliance", 1240, {
        delayDetails: { duration: 72, unit: "hours" },
      }),
      slackNode(
        "7",
        "Escalate overdue critical secrets to security lead",
        1480,
        ":rotating_light: *SECRET ROTATION ESCALATION*\nOverdue secrets: {overdue_count} (Critical: {critical_overdue})\nTotal secrets checked: {total_checked}\n\nThese secrets remain unrotated after 72-hour grace period:\n{overdue_secrets}\n\n@security-lead — immediate action required for critical-tier secrets. Non-rotation of critical secrets violates our compliance requirements.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Daily Vault secret enumeration with risk-tiered rotation policy enforcement. Emails secret owners when rotation is overdue, waits 72 hours for compliance, then escalates unrotated critical secrets to the security lead via Slack.",
  },

  // ─────────────────────────────────────────────────────────
  // 8. Security Audit Bot
  // ─────────────────────────────────────────────────────────
  "Security Audit Bot": {
    nodes: [
      trigger("Employee submits security question", "webhook_app", {
        source: "chat-platform",
        event: "message.received",
        howItStarts:
          "Fires when an employee submits a security-related question through the internal chat widget, Slack, or web form.",
        setup:
          "Connect your chat platform or Slack bot to POST employee security questions to this webhook URL. Configure Pinecone index with security policies.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Classify query intent: policy, vendor, incident, access, compliance",
        280,
        "You are a security query classifier. Analyze the employee's question and determine the intent category, urgency, and whether it touches sensitive topics that require human review.",
        "Classify this security question: {webhook_payload}. Return JSON with: query_text (string, the actual question), employee_id (string if available), category (one of: policy, vendor, incident, access, compliance, data_protection, other), urgency (low/medium/high), is_sensitive (boolean, true if involves active incident, PII, credentials, or legal matters), key_terms (array of relevant security terms).",
        [
          { field: "query_text", as: "query_text", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "category", as: "query_category", type: "string" },
          { field: "urgency", as: "query_urgency", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "boolean" },
          { field: "key_terms", as: "key_terms", type: "string" },
        ],
      ),
      n("3", "hitApi", "Search security knowledge base in Pinecone", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_host}/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{query_embedding}" },
            { key: "topK", value: "8" },
            { key: "namespace", value: "security-policies" },
            { key: "includeMetadata", value: "true" },
            {
              key: "filter",
              value: '{"category": {"$eq": "{query_category}"}}',
            },
          ],
          variables: [
            { name: "matches", customName: "policy_matches", type: "string" },
            {
              name: "matches[0].score",
              customName: "top_match_score",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "Generate grounded answer with citations and confidence",
        760,
        "You are a security knowledge assistant. Generate a helpful, accurate answer based ONLY on the retrieved policy documents. Include specific citations. If the retrieved documents do not adequately address the question, clearly state that and set confidence low.",
        "Employee question: {query_text}. Category: {query_category}. Retrieved policy documents: {policy_matches}. Generate JSON with: answer (string with clear, actionable response), citations (array of objects with source_title and section), confidence (number 0-1), follow_up_suggestion (string if the employee might need additional help), requires_human_review (boolean).",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "citations", as: "answer_citations", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          {
            field: "requires_human_review",
            as: "requires_human_review",
            type: "boolean",
          },
        ],
      ),
      n("5", "condition", "Is confidence below 0.7 or topic sensitive?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "answer_confidence", operator: "less_than", value: "0.7" },
            { field: "is_sensitive", operator: "equals", value: "true" },
          ],
          logic: "or",
        },
      }),
      slackNode(
        "6",
        "Route to security team for human review",
        1240,
        ":lock: *Security Bot — Human Review Required*\nEmployee: {employee_id}\nCategory: {query_category} | Urgency: {query_urgency}\nQuestion: {query_text}\n\nBot Answer (confidence: {answer_confidence}):\n{generated_answer}\n\nCitations: {answer_citations}\nSensitive topic: {is_sensitive}\n\nPlease review and provide a verified response to the employee.",
      ),
      n("7", "runCode", "Log query, sources, and action taken", 1480, {
        runCodeDetails: {
          code: `const logEntry = { timestamp: new Date().toISOString(), employee: '{employee_id}', category: '{query_category}', urgency: '{query_urgency}', question: '{query_text}', confidence: {answer_confidence}, sensitive: {is_sensitive}, action: {answer_confidence} < 0.7 || {is_sensitive} ? 'escalated_to_human' : 'auto_answered', citations_count: JSON.parse('{answer_citations}').length }; return { log_entry: JSON.stringify(logEntry), action_taken: logEntry.action };`,
          outputs: [
            { name: "log_entry", type: "string" },
            { name: "action_taken", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Log for periodic insights digest of top topics and gaps",
        1720,
        "SecurityBotLog",
        '[["{employee_id}", "{query_category}", "{query_urgency}", "{query_text}", "{answer_confidence}", "{is_sensitive}", "{action_taken}", "{answer_citations}", "=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "AI-powered security knowledge bot that classifies employee questions, searches security policies in Pinecone, generates grounded answers with citations, routes low-confidence or sensitive queries to the security team via Slack, and logs all interactions for gap analysis.",
  },

  // ─────────────────────────────────────────────────────────
  // 9. Security Awareness Training Reminder
  // ─────────────────────────────────────────────────────────
  "Security Awareness Training Reminder": {
    nodes: [
      trigger("Daily training compliance check at 9 AM", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs every morning at 9 AM to check training assignments and completion status from the learning management system.",
        setup:
          "Configure KnowBe4 (or your LMS) API access. Define training modules and completion requirements.",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch training assignments from KnowBe4 LMS", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.knowbe4.com/v1/training/enrollments?status=not_started,in_progress&per_page=500",
          headers: [
            { key: "Authorization", value: "Bearer {knowbe4_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "enrollments",
              customName: "training_enrollments",
              type: "string",
            },
            {
              name: "enrollments.length",
              customName: "incomplete_count",
              type: "number",
            },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Classify employees by status and role-based priority",
        520,
        {
          runCodeDetails: {
            code: `const enrollments = JSON.parse('{training_enrollments}'); const now = new Date(); const classified = enrollments.map(e => { const due = new Date(e.enrollment_date); due.setDate(due.getDate() + (e.training_campaign?.duration_days || 30)); const daysLeft = Math.ceil((due - now) / 86400000); const overdue = daysLeft < 0; const rolePriority = ['admin', 'engineering', 'finance', 'hr'].includes(e.user?.department?.toLowerCase()) ? 'high' : 'standard'; return { email: e.user?.email, name: e.user?.first_name + ' ' + e.user?.last_name, department: e.user?.department, manager_email: e.user?.manager_email, module: e.module_name, status: e.status, daysLeft, overdue, rolePriority, dueDate: due.toISOString().split('T')[0] }; }); const pastDue = classified.filter(c => c.overdue); return { classified_users: JSON.stringify(classified), past_due_users: JSON.stringify(pastDue), past_due_count: pastDue.length, has_past_due: pastDue.length > 0, high_priority_overdue: pastDue.filter(p => p.rolePriority === 'high').length };`,
            outputs: [
              { name: "classified_users", type: "string" },
              { name: "past_due_users", type: "string" },
              { name: "past_due_count", type: "number" },
              { name: "has_past_due", type: "boolean" },
              { name: "high_priority_overdue", type: "number" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Any employees with incomplete training past due?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "has_past_due", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Send personalized reminder with direct training link",
        1000,
        "{employee_email}",
        "Friendly Reminder: Complete your security awareness training by {dueDate}",
        'Hi {employee_name},\n\nThis is a friendly reminder that your security awareness training module "{module}" is due for completion.\n\nDue Date: {dueDate}\nEstimated Time: 15-20 minutes\n\nComplete your training here: https://training.knowbe4.com/ui/login\n\nSecurity awareness training is required for all employees and helps protect our organization from phishing, social engineering, and other cyber threats.\n\nIf you\'ve already completed the training, please disregard this message — it may take up to 24 hours for completion status to sync.\n\nThank you for helping keep our organization secure!\n\nSecurity Team',
      ),
      n("6", "delayAction", "Wait 5 days for training completion", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      slackNode(
        "7",
        "DM manager with team completion summary for escalation",
        1480,
        ":books: *Security Training Escalation — Manager Action Required*\n\nPast-due training assignments: {past_due_count}\nHigh-priority roles overdue: {high_priority_overdue}\n\nOverdue employees:\n{past_due_users}\n\nThese employees have not completed their required security awareness training after multiple reminders. Please follow up directly with your team members.\n\nNon-completion affects our compliance posture and may be reported to HR if unresolved within 5 additional business days.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Daily security awareness training compliance enforcement: fetches assignments from KnowBe4 LMS, classifies employees by role-based priority, sends personalized reminder emails with direct training links, waits 5 days, then escalates to managers via Slack DM with team completion summaries.",
  },

  // ─────────────────────────────────────────────────────────
  // 10. Security Incident Response Bot
  // ─────────────────────────────────────────────────────────
  "Security Incident Response Bot": {
    nodes: [
      trigger("Incident report received via Slack/web/email", "webhook_app", {
        source: "incident-reporting",
        event: "incident.reported",
        howItStarts:
          "Fires when a security incident is reported through any channel — Slack bot command, web form, or email to security@.",
        setup:
          "Configure incident reporting channels to POST to this webhook. Set up PagerDuty integration and Slack API access.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract incident details and classify severity",
        280,
        "You are a security incident classifier. Extract all relevant details from the incident report and classify severity using the NIST incident handling framework. Consider scope, data sensitivity, and business impact.",
        "Analyze this incident report: {webhook_payload}. Return JSON with: incident_title (string), reporter_name (string), reporter_email (string), incident_type (one of: malware, phishing, data_breach, unauthorized_access, ddos, insider_threat, ransomware, account_compromise, other), description (string), affected_systems (array of strings), affected_data_types (array: PII, financial, health, credentials, IP, none), severity (critical/high/medium/low), estimated_impact (string), time_detected (string), indicators_of_compromise (array of strings).",
        [
          { field: "incident_title", as: "incident_title", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "description", as: "incident_description", type: "string" },
          { field: "affected_systems", as: "affected_systems", type: "string" },
          {
            field: "affected_data_types",
            as: "affected_data_types",
            type: "string",
          },
          { field: "severity", as: "incident_severity", type: "string" },
          { field: "estimated_impact", as: "estimated_impact", type: "string" },
          { field: "indicators_of_compromise", as: "iocs", type: "string" },
        ],
      ),
      n("3", "runCode", "Select runbook by incident type and severity", 520, {
        runCodeDetails: {
          code: `const type = '{incident_type}'; const severity = '{incident_severity}'; const runbooks = { malware: { steps: ['Isolate affected endpoints', 'Capture memory dumps', 'Run full AV scan', 'Check lateral movement', 'Restore from clean backup'], containment: 'network_isolation', forensics: true }, phishing: { steps: ['Block sender domain', 'Search for other recipients', 'Reset compromised credentials', 'Check for payload execution', 'Update email filters'], containment: 'credential_reset', forensics: false }, data_breach: { steps: ['Identify data scope', 'Preserve evidence', 'Assess notification requirements', 'Engage legal counsel', 'Prepare breach notification'], containment: 'access_revocation', forensics: true }, unauthorized_access: { steps: ['Revoke access immediately', 'Audit access logs', 'Check for data exfiltration', 'Reset credentials', 'Review access controls'], containment: 'access_revocation', forensics: true }, ransomware: { steps: ['Disconnect affected systems', 'Do NOT pay ransom', 'Preserve encrypted files', 'Identify ransomware variant', 'Restore from offline backups'], containment: 'network_isolation', forensics: true } }; const runbook = runbooks[type] || runbooks.malware; const escalation = severity === 'critical' ? 'immediate' : severity === 'high' ? '30_minutes' : '2_hours'; return { runbook_steps: JSON.stringify(runbook.steps), containment_action: runbook.containment, requires_forensics: runbook.forensics, escalation_timeline: escalation, is_critical: severity === 'critical' || severity === 'high' };`,
          outputs: [
            { name: "runbook_steps", type: "string" },
            { name: "containment_action", type: "string" },
            { name: "requires_forensics", type: "boolean" },
            { name: "escalation_timeline", type: "string" },
            { name: "is_critical", type: "boolean" },
          ],
        },
      }),
      n("4", "hitApi", "Page on-call responders via PagerDuty", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pagerduty.com/incidents",
          headers: [
            { key: "Authorization", value: "Token token={pagerduty_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "From", value: "security-bot@company.com" },
          ],
          body: [
            {
              key: "incident",
              value:
                '{"type": "incident", "title": "Security Incident: {incident_title}", "service": {"id": "{pagerduty_service_id}", "type": "service_reference"}, "urgency": "{incident_severity}", "body": {"type": "incident_body", "details": "Type: {incident_type}\\nSeverity: {incident_severity}\\nSystems: {affected_systems}\\nDescription: {incident_description}\\nRunbook: {runbook_steps}"}}',
            },
          ],
          variables: [
            {
              name: "incident.id",
              customName: "pd_incident_id",
              type: "string",
            },
            {
              name: "incident.html_url",
              customName: "pd_incident_url",
              type: "string",
            },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Create Slack war room channel and invite responders",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://slack.com/api/conversations.create",
            headers: [
              { key: "Authorization", value: "Bearer {slack_bot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "name",
                value: "incident-{incident_type}-{pd_incident_id}",
              },
              { key: "is_private", value: "true" },
            ],
            variables: [
              {
                name: "channel.id",
                customName: "war_room_channel_id",
                type: "string",
              },
              {
                name: "channel.name",
                customName: "war_room_name",
                type: "string",
              },
            ],
          },
        },
      ),
      n("6", "condition", "Is severity critical or high?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "is_critical", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "7",
        "approvalGate",
        "Incident commander approves containment actions",
        1480,
        {
          approvalDetails: {
            approver: "incident-commander",
            timeout: 2,
            timeoutUnit: "hours",
            message:
              "SECURITY INCIDENT: {incident_title}\nSeverity: {incident_severity}\nType: {incident_type}\nAffected: {affected_systems}\nContainment action: {containment_action}\nRunbook: {runbook_steps}\nPagerDuty: {pd_incident_url}\nWar Room: #{war_room_name}\n\nApprove containment actions?",
          },
        },
      ),
      n("8", "hitApi", "Preserve logs and forensic artifacts via AWS", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://logs.us-east-1.amazonaws.com/",
          headers: [
            { key: "Authorization", value: "{aws_sig_v4}" },
            { key: "Content-Type", value: "application/x-amz-json-1.1" },
            { key: "X-Amz-Target", value: "Logs_20140328.CreateExportTask" },
          ],
          body: [
            { key: "logGroupName", value: "/aws/security/incidents" },
            { key: "destination", value: "security-forensics-bucket" },
            { key: "destinationPrefix", value: "incident-{pd_incident_id}" },
            { key: "from", value: "{incident_start_epoch}" },
            { key: "to", value: "{current_epoch}" },
          ],
          variables: [
            { name: "taskId", customName: "export_task_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "9",
        "Broadcast internal status update",
        1960,
        ":rotating_light: *SECURITY INCIDENT STATUS UPDATE*\n\n*Incident:* {incident_title}\n*Severity:* {incident_severity}\n*Type:* {incident_type}\n*Affected Systems:* {affected_systems}\n*Data Types:* {affected_data_types}\n*Containment:* {containment_action}\n*PagerDuty:* {pd_incident_url}\n*War Room:* #{war_room_name}\n*Forensics:* Export task {export_task_id}\n\nAll updates will be posted to the war room channel. Do not discuss incident details outside the war room.",
      ),
      sheetsNode(
        "10",
        "Record incident metrics and ticket linkage",
        2200,
        "IncidentLog",
        '[["{pd_incident_id}", "{incident_title}", "{incident_type}", "{incident_severity}", "{affected_systems}", "{affected_data_types}", "{containment_action}", "{requires_forensics}", "{export_task_id}", "{reporter_email}", "=NOW()", "active"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "9", "output_2"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
    summary:
      "Complete security incident response orchestration: AI-classifies severity, selects appropriate runbook, pages responders via PagerDuty, creates private Slack war room, routes critical incidents through commander approval for containment, preserves forensic artifacts in AWS, broadcasts status updates, and logs all incident metrics.",
  },

  // ─────────────────────────────────────────────────────────
  // 11. Security Policy Acknowledgment Tracker
  // ─────────────────────────────────────────────────────────
  "Security Policy Acknowledgment Tracker": {
    nodes: [
      trigger("New policy version published", "webhook_app", {
        source: "policy-management",
        event: "policy.published",
        howItStarts:
          "Fires when a new version of a security policy is published in the policy management system.",
        setup:
          "Configure your policy management platform to POST to this webhook when a policy version is published. Ensure Microsoft Graph API access for employee directory.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Resolve audience by role and employment status via Microsoft Graph",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.microsoft.com/v1.0/users?$filter=accountEnabled eq true&$select=displayName,mail,department,jobTitle,employeeType&$top=999",
            headers: [
              { key: "Authorization", value: "Bearer {ms_graph_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "value", customName: "employee_list", type: "string" },
              {
                name: "value.length",
                customName: "total_employees",
                type: "number",
              },
            ],
          },
        },
      ),
      emailNode(
        "3",
        "Send personalized acknowledgment request",
        520,
        "{employee_email}",
        "Action Required: Acknowledge Updated Security Policy — {policy_name}",
        "Dear {employee_name},\n\nA new version of the following security policy has been published and requires your acknowledgment:\n\nPolicy: {policy_name}\nVersion: {policy_version}\nEffective Date: {effective_date}\n\nKey Changes Summary:\n{policy_summary}\n\nPlease review the full policy and acknowledge receipt by clicking the link below:\n{acknowledgment_url}\n\nYou have 72 hours to complete this acknowledgment. Failure to acknowledge may result in escalation to your manager.\n\nThank you for your attention to information security.\n\nSecurity & Compliance Team",
      ),
      n("4", "delayAction", "Wait 72 hours for acknowledgment", 760, {
        delayDetails: { duration: 72, unit: "hours" },
      }),
      n(
        "5",
        "runCode",
        "Track acknowledgment progress with department rollup",
        1000,
        {
          runCodeDetails: {
            code: `const employees = JSON.parse('{employee_list}'); const acknowledged = JSON.parse('{acknowledged_list}' || '[]'); const ackEmails = new Set(acknowledged.map(a => a.email)); const departments = {}; employees.forEach(emp => { const dept = emp.department || 'Unknown'; if (!departments[dept]) departments[dept] = { total: 0, acknowledged: 0, pending: [] }; departments[dept].total++; if (ackEmails.has(emp.mail)) { departments[dept].acknowledged++; } else { departments[dept].pending.push(emp.mail); } }); const deptSummary = Object.entries(departments).map(([name, d]) => ({ department: name, total: d.total, acknowledged: d.acknowledged, rate: ((d.acknowledged / d.total) * 100).toFixed(1), pending: d.pending })); const overallRate = ((acknowledged.length / employees.length) * 100).toFixed(1); const lowDepts = deptSummary.filter(d => parseFloat(d.rate) < 90); return { department_summary: JSON.stringify(deptSummary), overall_rate: parseFloat(overallRate), low_completion_depts: JSON.stringify(lowDepts), has_low_completion: lowDepts.length > 0, pending_count: employees.length - acknowledged.length };`,
            outputs: [
              { name: "department_summary", type: "string" },
              { name: "overall_rate", type: "number" },
              { name: "low_completion_depts", type: "string" },
              { name: "has_low_completion", type: "boolean" },
              { name: "pending_count", type: "number" },
            ],
          },
        },
      ),
      n("6", "condition", "Any department below 90% completion?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "has_low_completion", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "7",
        "Send reminder escalation to department managers",
        1480,
        ":memo: *Policy Acknowledgment Escalation*\n\nPolicy: {policy_name} (v{policy_version})\nOverall acknowledgment rate: {overall_rate}%\nPending acknowledgments: {pending_count}\n\nDepartments below 90% completion:\n{low_completion_depts}\n\nManagers: please follow up with your team members who have not yet acknowledged this policy. Compliance audit requires 100% acknowledgment within the policy effective date.",
      ),
      sheetsNode(
        "8",
        "Export audit-ready acknowledgment evidence",
        1720,
        "PolicyAcknowledgments",
        '[["{policy_name}", "{policy_version}", "{total_employees}", "{overall_rate}", "{pending_count}", "{low_completion_depts}", "=NOW()", "tracking_active"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "End-to-end security policy acknowledgment tracking: resolves employee audience from Microsoft Graph, sends personalized acknowledgment requests via email, waits 72 hours, computes department-level completion rollups, escalates low-completion departments to managers via Slack, and exports audit-ready evidence.",
  },

  // ─────────────────────────────────────────────────────────
  // 12. Security Vulnerability Scanner Alert
  // ─────────────────────────────────────────────────────────
  "Security Vulnerability Scanner Alert": {
    nodes: [
      trigger(
        "Vulnerability scan results received from Snyk/Qualys",
        "webhook_app",
        {
          source: "vulnerability-scanner",
          event: "scan.completed",
          howItStarts:
            "Fires when a vulnerability scan completes and results are delivered via webhook from Snyk, Qualys, or similar scanner.",
          setup:
            "Configure your vulnerability scanner (Snyk, Qualys, Tenable) to POST scan results to this webhook URL upon completion.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "Extract and deduplicate findings by asset/CVE fingerprint",
        280,
        "You are a vulnerability analyst. Parse the scanner output, extract unique findings, and deduplicate by CVE ID and asset. Prioritize by exploitability and business context.",
        "Parse these vulnerability scan results: {webhook_payload}. Return JSON with: scan_source (string like Snyk or Qualys), scan_date (string), findings (array of objects with cve_id, title, severity critical/high/medium/low, affected_asset, package_name, current_version, fixed_version, cvss_score number), total_findings (number), critical_count (number), high_count (number), unique_cves (number).",
        [
          { field: "scan_source", as: "scan_source", type: "string" },
          { field: "findings", as: "vuln_findings", type: "string" },
          { field: "total_findings", as: "total_findings", type: "number" },
          { field: "critical_count", as: "critical_count", type: "number" },
          { field: "high_count", as: "high_count", type: "number" },
          { field: "unique_cves", as: "unique_cves", type: "number" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Enrich with CVSS score and exploit status from NVD",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://services.nvd.nist.gov/rest/json/cves/2.0?cveId={top_cve_ids}",
            headers: [
              { key: "apiKey", value: "{nvd_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "vulnerabilities",
                customName: "nvd_enrichment",
                type: "string",
              },
              {
                name: "vulnerabilities.length",
                customName: "nvd_matches",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "runCode",
        "Filter critical and known-exploited vulnerabilities",
        760,
        {
          runCodeDetails: {
            code: `const findings = JSON.parse('{vuln_findings}'); const nvd = JSON.parse('{nvd_enrichment}'); const exploitedCves = new Set(nvd.filter(v => v.cve?.metrics?.cvssMetricV31?.[0]?.exploitabilityScore > 3.5).map(v => v.cve.id)); const critical = findings.filter(f => { const isCritical = f.severity === 'critical' || f.cvss_score >= 9.0; const isExploited = exploitedCves.has(f.cve_id); return isCritical || isExploited; }).map(f => ({ ...f, is_exploited: exploitedCves.has(f.cve_id), priority: exploitedCves.has(f.cve_id) ? 'P0_exploited' : f.severity === 'critical' ? 'P1_critical' : 'P2_high' })); return { critical_exploited: JSON.stringify(critical), critical_exploited_count: critical.length, has_critical_exploited: critical.length > 0, p0_count: critical.filter(c => c.priority === 'P0_exploited').length };`,
            outputs: [
              { name: "critical_exploited", type: "string" },
              { name: "critical_exploited_count", type: "number" },
              { name: "has_critical_exploited", type: "boolean" },
              { name: "p0_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Any critical or actively exploited vulnerabilities?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "has_critical_exploited",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Alert security team with high-signal finding details",
        1240,
        ":rotating_light: *Critical Vulnerability Alert*\nSource: {scan_source}\nTotal findings: {total_findings} | Critical: {critical_count} | High: {high_count}\nP0 Exploited: {p0_count}\n\nCritical/Exploited findings requiring immediate action:\n{critical_exploited}\n\nImmediate remediation is required for P0 items. SLA: 24 hours for exploited, 72 hours for critical.",
      ),
      n(
        "7",
        "hitApi",
        "Create remediation ticket in Jira with SLA and evidence",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://company.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_credentials}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project": {"key": "SEC"}, "summary": "Vulnerability Remediation: {critical_exploited_count} critical findings from {scan_source}", "issuetype": {"name": "Bug"}, "priority": {"name": "Highest"}, "description": {"type": "doc", "version": 1, "content": [{"type": "paragraph", "content": [{"type": "text", "text": "Scanner: {scan_source}. Total findings: {total_findings}. Critical/exploited: {critical_exploited_count}. P0 exploited: {p0_count}. Details: {critical_exploited}. SLA: P0=24h, P1=72h, P2=7d."}]}]}, "labels": ["vulnerability", "automated", "{scan_source}"]}',
              },
            ],
            variables: [
              { name: "id", customName: "jira_ticket_id", type: "string" },
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Monitor SLA compliance and track remediation",
        1720,
        "VulnTracking",
        '[["{scan_source}", "{total_findings}", "{critical_count}", "{high_count}", "{unique_cves}", "{critical_exploited_count}", "{p0_count}", "{jira_ticket_key}", "=NOW()", "open"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Automated vulnerability management pipeline: parses scanner results, deduplicates by CVE/asset, enriches with NVD exploit data, filters critical and known-exploited items, alerts security team via Slack, creates Jira remediation tickets with SLA, and tracks remediation compliance.",
  },

  // ─────────────────────────────────────────────────────────
  // 13. Suspicious Login Alert
  // ─────────────────────────────────────────────────────────
  "Suspicious Login Alert": {
    nodes: [
      trigger("Auth event from Okta/Auth0 stream", "webhook_app", {
        source: "auth-provider",
        event: "user.authentication",
        howItStarts:
          "Fires on every authentication event from the identity provider (Okta, Auth0) for real-time login anomaly detection.",
        setup:
          "Configure Okta Event Hooks or Auth0 Log Streams to POST authentication events to this webhook URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Analyze login signals: impossible travel, new device, TOR, unusual time",
        280,
        "You are a security anomaly detector specializing in authentication events. Analyze the login event for suspicious signals including impossible travel, new device fingerprints, TOR/VPN usage, unusual time of access, and geographic anomalies.",
        "Analyze this authentication event: {webhook_payload}. Return JSON with: user_email (string), user_id (string), login_ip (string), login_country (string), login_city (string), device_fingerprint (string), browser (string), os (string), login_timestamp (string), is_new_device (boolean), is_tor_exit (boolean), is_vpn (boolean), is_impossible_travel (boolean, if location changed too fast from last known login), is_unusual_time (boolean, outside normal business hours for user timezone), is_new_country (boolean), anomaly_signals (array of strings describing each anomaly found), signal_count (number of anomaly signals).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "login_ip", as: "login_ip", type: "string" },
          { field: "login_country", as: "login_country", type: "string" },
          { field: "login_city", as: "login_city", type: "string" },
          { field: "is_new_device", as: "is_new_device", type: "boolean" },
          { field: "is_tor_exit", as: "is_tor_exit", type: "boolean" },
          {
            field: "is_impossible_travel",
            as: "is_impossible_travel",
            type: "boolean",
          },
          { field: "anomaly_signals", as: "anomaly_signals", type: "string" },
          { field: "signal_count", as: "signal_count", type: "number" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Compute weighted risk score from anomaly signals",
        520,
        {
          runCodeDetails: {
            code: `const signals = { is_new_device: { weight: 15, active: {is_new_device} }, is_tor_exit: { weight: 30, active: {is_tor_exit} }, is_impossible_travel: { weight: 35, active: {is_impossible_travel} }, is_new_country: { weight: 20, active: false }, is_unusual_time: { weight: 10, active: false } }; let score = 0; const activeSignals = []; Object.entries(signals).forEach(([name, s]) => { if (s.active) { score += s.weight; activeSignals.push(name); } }); const anomalyList = JSON.parse('{anomaly_signals}'); anomalyList.forEach(() => { if (score < 100) score += 5; }); score = Math.min(score, 100); const riskLevel = score > 75 ? 'critical' : score > 50 ? 'high' : score > 25 ? 'medium' : 'low'; return { risk_score: score, risk_level: riskLevel, active_signals: JSON.stringify(activeSignals), is_suspicious: score > 75, recommended_action: score > 75 ? 'block_and_verify' : score > 50 ? 'challenge_mfa' : 'monitor' };`,
            outputs: [
              { name: "risk_score", type: "number" },
              { name: "risk_level", type: "string" },
              { name: "active_signals", type: "string" },
              { name: "is_suspicious", type: "boolean" },
              { name: "recommended_action", type: "string" },
            ],
          },
        },
      ),
      n("4", "condition", "Is risk score above 75 (suspicious)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "risk_score", operator: "greater_than", value: "75" },
          ],
        },
      }),
      emailNode(
        "5",
        "Send 'Was this you?' verification to user",
        1000,
        "{user_email}",
        "Security Alert: Unusual login detected on your account",
        "Hi,\n\nWe detected an unusual login to your account that we'd like you to verify.\n\nLogin Details:\n- Time: {login_timestamp}\n- Location: {login_city}, {login_country}\n- IP Address: {login_ip}\n- Risk Score: {risk_score}/100\n- Signals: {anomaly_signals}\n\nWas this you?\n\n[YES, THIS WAS ME] — {verification_url}?action=confirm\n[NO, SECURE MY ACCOUNT] — {verification_url}?action=lockdown\n\nIf you did not initiate this login, click 'Secure My Account' immediately. Your account will be temporarily locked and our security team will assist you.\n\nSecurity Team",
      ),
      slackNode(
        "6",
        "SOC alert with full login context and recommended action",
        1240,
        ":warning: *Suspicious Login Detected*\n\nUser: {user_email} ({user_id})\nIP: {login_ip} | Location: {login_city}, {login_country}\nRisk Score: {risk_score}/100 ({risk_level})\n\nAnomalies: {anomaly_signals}\nActive signals: {active_signals}\nRecommended action: {recommended_action}\n\nNew device: {is_new_device} | TOR: {is_tor_exit} | Impossible travel: {is_impossible_travel}\n\nUser has been sent a verification email. Monitor for response.",
      ),
      n(
        "7",
        "hitApi",
        "Page SOC team via PagerDuty for confirmed suspicious activity",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.pagerduty.com/incidents",
            headers: [
              { key: "Authorization", value: "Token token={pagerduty_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "From", value: "security-bot@company.com" },
            ],
            body: [
              {
                key: "incident",
                value:
                  '{"type": "incident", "title": "Suspicious Login: {user_email} from {login_country} (score {risk_score})", "service": {"id": "{pagerduty_soc_service_id}", "type": "service_reference"}, "urgency": "high", "body": {"type": "incident_body", "details": "Risk: {risk_level}. Signals: {anomaly_signals}. IP: {login_ip}. Action: {recommended_action}"}}',
              },
            ],
            variables: [
              {
                name: "incident.id",
                customName: "pd_incident_id",
                type: "string",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Log incident record with resolution status",
        1720,
        "SuspiciousLoginLog",
        '[["{user_email}", "{login_ip}", "{login_country}", "{login_city}", "{risk_score}", "{risk_level}", "{anomaly_signals}", "{recommended_action}", "{pd_incident_id}", "=NOW()", "investigating"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Real-time login anomaly detection: analyzes authentication events for impossible travel, TOR usage, new devices, and unusual patterns, computes weighted risk scores, sends user verification emails, alerts SOC via Slack with full context, pages PagerDuty for high-risk events, and logs all incidents.",
  },

  // ─────────────────────────────────────────────────────────
  // 14. Terms of Service Change Detector
  // ─────────────────────────────────────────────────────────
  "Terms of Service Change Detector": {
    nodes: [
      trigger("Daily ToS monitoring at 6 AM", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs every morning at 6 AM to crawl monitored vendor Terms of Service pages and detect changes.",
        setup:
          "Maintain a list of vendor ToS URLs in the Google Sheets configuration. Ensure previous snapshots are stored for comparison.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Crawl vendor ToS pages and capture content snapshots",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.apify.com/v2/acts/apify~web-scraper/runs?token={apify_token}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "startUrls", value: "{tos_url_list}" },
              {
                key: "pageFunction",
                value:
                  'async function pageFunction(context) { return { url: context.request.url, title: document.title, content: document.querySelector("main, article, .content, body").innerText.substring(0, 50000), capturedAt: new Date().toISOString() }; }',
              },
            ],
            variables: [
              { name: "data", customName: "tos_snapshots", type: "string" },
              {
                name: "data.length",
                customName: "pages_crawled",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "Perform semantic and clause-level diff analysis",
        520,
        "You are a legal document analyst specializing in Terms of Service comparison. Compare current snapshots against previous versions and identify all changes at the clause level. Focus on material changes that affect user rights, liability, data usage, and dispute resolution.",
        "Current ToS snapshots: {tos_snapshots}. Previous snapshots: {previous_snapshots}. For each vendor, compare and return JSON with: vendor_changes (array of objects with vendor_name, url, changes array of objects with clause_area, previous_text, current_text, change_type added/removed/modified, significance cosmetic/material/critical), vendors_with_changes (number), total_changes (number).",
        [
          { field: "vendor_changes", as: "vendor_changes", type: "string" },
          {
            field: "vendors_with_changes",
            as: "vendors_with_changes",
            type: "number",
          },
          { field: "total_changes", as: "total_changes", type: "number" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Classify change significance: cosmetic vs material vs critical",
        760,
        {
          runCodeDetails: {
            code: `const changes = JSON.parse('{vendor_changes}'); const material = []; const critical = []; changes.forEach(vendor => { vendor.changes.forEach(c => { if (c.significance === 'critical') critical.push({ vendor: vendor.vendor_name, ...c }); else if (c.significance === 'material') material.push({ vendor: vendor.vendor_name, ...c }); }); }); const hasMaterialChanges = material.length > 0 || critical.length > 0; return { material_changes: JSON.stringify(material), critical_changes: JSON.stringify(critical), material_count: material.length, critical_count: critical.length, has_material_changes: hasMaterialChanges, summary: hasMaterialChanges ? critical.length + ' critical, ' + material.length + ' material changes detected' : 'No material changes detected' };`,
            outputs: [
              { name: "material_changes", type: "string" },
              { name: "critical_changes", type: "string" },
              { name: "material_count", type: "number" },
              { name: "critical_count", type: "number" },
              { name: "has_material_changes", type: "boolean" },
              { name: "summary", type: "string" },
            ],
          },
        },
      ),
      n("5", "condition", "Any material or critical changes detected?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "has_material_changes",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      emailNode(
        "6",
        "Email legal team with highlighted changes and risk assessment",
        1240,
        "{legal_team_email}",
        "Vendor ToS Changes Detected: {summary}",
        "Legal Team,\n\nOur daily Terms of Service monitoring has detected material changes across vendor agreements.\n\nSummary: {summary}\nVendors with changes: {vendors_with_changes}\nTotal changes: {total_changes}\n\nCritical Changes (require immediate review):\n{critical_changes}\n\nMaterial Changes (review within 5 business days):\n{material_changes}\n\nPlease review these changes and determine if any require:\n1. Contract renegotiation\n2. Internal policy updates\n3. Customer notification\n4. Vendor relationship reassessment\n\nToS Monitoring System",
      ),
      slackNode(
        "7",
        "Notify affected stakeholders by function",
        1480,
        ":page_facing_up: *Vendor ToS Change Alert*\n\n{summary}\n\nCritical changes: {critical_count}\nMaterial changes: {material_count}\nPages crawled: {pages_crawled}\n\nCritical details: {critical_changes}\n\nLegal team has been notified. Affected stakeholders should review vendor-specific impacts relevant to their function.",
      ),
      sheetsNode(
        "8",
        "Archive version with diff and response status",
        1720,
        "ToSChangeLog",
        '[["{pages_crawled}", "{vendors_with_changes}", "{total_changes}", "{critical_count}", "{material_count}", "{summary}", "{vendor_changes}", "=NOW()", "pending_review"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Daily vendor Terms of Service monitoring: crawls ToS pages, performs AI semantic diff analysis at the clause level, classifies changes as cosmetic/material/critical, emails legal team with highlighted changes and risk assessment, notifies stakeholders via Slack, and archives all versions with diffs.",
  },

  // ─────────────────────────────────────────────────────────
  // 15. Third Party Risk Assessment Bot
  // ─────────────────────────────────────────────────────────
  "Third Party Risk Assessment Bot": {
    nodes: [
      trigger("Vendor request form submitted", "webhook_form", {
        source: "vendor-intake",
        event: "form.submitted",
        howItStarts:
          "Fires when a business unit submits a new vendor onboarding or renewal request form.",
        setup:
          "Configure the vendor intake form to POST to this webhook URL. Ensure SecurityScorecard API access and internal vendor database connection.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract vendor details, data access level, criticality",
        280,
        "You are a third-party risk assessment intake processor. Extract all vendor details needed for a comprehensive risk assessment regardless of form field naming conventions.",
        "Extract from this vendor request: {webhook_payload}. Return JSON with: vendor_name (string), vendor_website (string), vendor_contact_email (string), requesting_team (string), requester_email (string), data_access_level (none/read_only/read_write/admin), data_types_shared (array: PII, financial, health, credentials, IP, none), integration_type (api, file_transfer, saas_login, on_premise, none), contract_value_annual (number), vendor_criticality (critical/high/medium/low), business_justification (string), existing_vendor (boolean).",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_website", as: "vendor_website", type: "string" },
          {
            field: "vendor_contact_email",
            as: "vendor_contact_email",
            type: "string",
          },
          { field: "requester_email", as: "requester_email", type: "string" },
          {
            field: "data_access_level",
            as: "data_access_level",
            type: "string",
          },
          {
            field: "data_types_shared",
            as: "data_types_shared",
            type: "string",
          },
          { field: "integration_type", as: "integration_type", type: "string" },
          {
            field: "vendor_criticality",
            as: "vendor_criticality",
            type: "string",
          },
          { field: "existing_vendor", as: "existing_vendor", type: "boolean" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Gather external intelligence from SecurityScorecard",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.securityscorecard.io/companies/{vendor_domain}/factors",
            headers: [
              {
                key: "Authorization",
                value: "Token {securityscorecard_token}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "overall_score",
                customName: "vendor_security_score",
                type: "number",
              },
              {
                name: "factors",
                customName: "security_factors",
                type: "string",
              },
              { name: "grade", customName: "vendor_grade", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "Generate tailored security questionnaire by data access and criticality",
        760,
        "You are a vendor risk assessment specialist. Generate a tailored security questionnaire based on the vendor's data access level, criticality, and the types of data being shared. More sensitive data access requires deeper questions.",
        "Vendor: {vendor_name}. Data access: {data_access_level}. Data types: {data_types_shared}. Integration: {integration_type}. Criticality: {vendor_criticality}. SecurityScorecard grade: {vendor_grade}. Score: {vendor_security_score}. Security factors: {security_factors}. Generate JSON with: questionnaire (array of objects with section like encryption/access_control/incident_response/compliance, question string, required boolean, evidence_requested string), total_questions (number), focus_areas (array of strings identifying areas of concern based on scorecard), estimated_completion_time (string).",
        [
          {
            field: "questionnaire",
            as: "vendor_questionnaire",
            type: "string",
          },
          { field: "total_questions", as: "total_questions", type: "number" },
          { field: "focus_areas", as: "focus_areas", type: "string" },
          {
            field: "estimated_completion_time",
            as: "estimated_completion_time",
            type: "string",
          },
        ],
      ),
      emailNode(
        "5",
        "Distribute questionnaire to vendor with secure response channel",
        1000,
        "{vendor_contact_email}",
        "Security Assessment Questionnaire — {vendor_name}",
        "Dear {vendor_name} Security Team,\n\nAs part of our vendor risk management program, we require completion of a security assessment questionnaire before we can proceed with onboarding/renewal.\n\nQuestionnaire Details:\n- Total questions: {total_questions}\n- Estimated completion time: {estimated_completion_time}\n- Focus areas: {focus_areas}\n\n{vendor_questionnaire}\n\nPlease submit your responses along with any supporting evidence (certifications, audit reports, policies) via our secure portal: {secure_portal_url}\n\nDeadline: 10 business days from receipt\n\nIf you have questions, contact our vendor security team at vendor-security@company.com.\n\nVendor Risk Management Team",
      ),
      aiNode(
        "6",
        "Analyze vendor responses and identify gaps",
        1240,
        "You are a vendor security analyst. Analyze the vendor's questionnaire responses, identify gaps and weaknesses, and assess overall vendor security posture. Compare against industry standards and our security requirements.",
        "Vendor: {vendor_name}. Questionnaire responses: {vendor_responses}. SecurityScorecard: {vendor_grade} ({vendor_security_score}). Data access: {data_access_level}. Data types: {data_types_shared}. Analyze and return JSON with: overall_assessment (strong/adequate/weak/unacceptable), gaps (array of objects with area, finding, severity high/medium/low, remediation_required boolean), strengths (array of strings), risk_factors (array of strings), certification_status (object with soc2, iso27001, gdpr each boolean), requires_remediation_plan (boolean).",
        [
          {
            field: "overall_assessment",
            as: "overall_assessment",
            type: "string",
          },
          { field: "gaps", as: "assessment_gaps", type: "string" },
          { field: "strengths", as: "vendor_strengths", type: "string" },
          {
            field: "requires_remediation_plan",
            as: "requires_remediation",
            type: "boolean",
          },
        ],
      ),
      n("7", "runCode", "Compute weighted risk score for vendor", 1480, {
        runCodeDetails: {
          code: `const assessment = '{overall_assessment}'; const gaps = JSON.parse('{assessment_gaps}'); const secScore = {vendor_security_score}; const criticality = '{vendor_criticality}'; const dataAccess = '{data_access_level}'; let score = 0; const assessmentWeights = { strong: 10, adequate: 30, weak: 60, unacceptable: 90 }; score += assessmentWeights[assessment] || 50; score += gaps.filter(g => g.severity === 'high').length * 10; score += gaps.filter(g => g.severity === 'medium').length * 5; if (secScore < 60) score += 20; else if (secScore < 75) score += 10; if (dataAccess === 'admin' || dataAccess === 'read_write') score += 15; if (criticality === 'critical') score += 10; score = Math.min(score, 100); const riskLevel = score > 70 ? 'high' : score > 40 ? 'medium' : 'low'; const requiresApproval = riskLevel === 'high' || riskLevel === 'medium'; return { vendor_risk_score: score, vendor_risk_level: riskLevel, requires_approval: requiresApproval, high_gaps: gaps.filter(g => g.severity === 'high').length, recommendation: score > 70 ? 'reject_or_remediate' : score > 40 ? 'approve_with_conditions' : 'approve' };`,
          outputs: [
            { name: "vendor_risk_score", type: "number" },
            { name: "vendor_risk_level", type: "string" },
            { name: "requires_approval", type: "boolean" },
            { name: "high_gaps", type: "number" },
            { name: "recommendation", type: "string" },
          ],
        },
      }),
      n(
        "8",
        "approvalGate",
        "Security owner reviews medium/high-risk vendors",
        1720,
        {
          approvalDetails: {
            approver: "vendor-security-owner",
            timeout: 48,
            timeoutUnit: "hours",
            message:
              "Vendor Risk Assessment: {vendor_name}\nRisk Score: {vendor_risk_score}/100 ({vendor_risk_level})\nSecurityScorecard: {vendor_grade} ({vendor_security_score})\nData Access: {data_access_level}\nCriticality: {vendor_criticality}\nAssessment: {overall_assessment}\nHigh-severity gaps: {high_gaps}\nRecommendation: {recommendation}\n\nGaps: {assessment_gaps}\nStrengths: {vendor_strengths}",
          },
        },
      ),
      sheetsNode(
        "9",
        "Update vendor risk register and schedule reassessment",
        1960,
        "VendorRiskRegister",
        '[["{vendor_name}", "{vendor_website}", "{vendor_criticality}", "{data_access_level}", "{data_types_shared}", "{vendor_grade}", "{vendor_security_score}", "{vendor_risk_score}", "{vendor_risk_level}", "{overall_assessment}", "{recommendation}", "{high_gaps}", "=NOW()", "=DATE(YEAR(NOW()),MONTH(NOW())+12,DAY(NOW()))"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary:
      "Comprehensive third-party risk assessment: extracts vendor details from intake form, gathers SecurityScorecard intelligence, generates tailored security questionnaires, distributes to vendors, analyzes responses with AI, computes weighted risk scores, routes medium/high-risk vendors through security approval, and maintains the risk register with reassessment scheduling.",
  },

  // ─────────────────────────────────────────────────────────
  // 16. Vendor Security Assessment Bot
  // ─────────────────────────────────────────────────────────
  "Vendor Security Assessment Bot": {
    nodes: [
      trigger("Vendor security question submitted", "webhook_app", {
        source: "procurement-engineering",
        event: "question.submitted",
        howItStarts:
          "Fires when procurement or engineering submits a question about a vendor's security posture through the internal tool or Slack command.",
        setup:
          "Connect Slack command or internal tool to POST vendor questions to this webhook. Ensure vendor assessment database and Jira are configured.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract vendor name and question context from payload",
        280,
        "You are a vendor security query parser. Extract the vendor name, specific question, and context from the submitted inquiry regardless of format.",
        "Parse this vendor security question: {webhook_payload}. Return JSON with: vendor_name (string), vendor_domain (string if identifiable), question_text (string), question_category (one of: encryption, compliance, data_handling, access_control, incident_response, general), asker_name (string), asker_email (string), urgency (low/medium/high based on question context).",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_domain", as: "vendor_domain", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          {
            field: "question_category",
            as: "question_category",
            type: "string",
          },
          { field: "asker_email", as: "asker_email", type: "string" },
          { field: "urgency", as: "question_urgency", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Look up existing vendor assessment in internal database",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.company.com/vendor-assessments?vendor_name={vendor_name}&include=latest_assessment,certifications,findings",
            headers: [
              { key: "Authorization", value: "Bearer {internal_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "assessment",
                customName: "existing_assessment",
                type: "string",
              },
              {
                name: "assessment.last_updated",
                customName: "assessment_date",
                type: "string",
              },
              {
                name: "assessment.risk_level",
                customName: "vendor_risk_level",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "Synthesize grounded answer with confidence and citations",
        760,
        "You are a vendor security knowledge assistant. Generate an accurate, grounded answer based on the existing vendor assessment data. Include citations to specific assessment sections. If the data is stale (over 12 months) or does not address the question, indicate low confidence.",
        "Question about {vendor_name}: {question_text}. Category: {question_category}. Existing assessment data: {existing_assessment}. Assessment date: {assessment_date}. Risk level: {vendor_risk_level}. Generate JSON with: answer (string with clear, specific response), confidence (number 0-1), citations (array of objects with source and section), data_freshness (fresh/aging/stale based on assessment_date), caveats (array of strings noting any limitations of the answer), needs_reassessment (boolean if data is stale or insufficient).",
        [
          { field: "answer", as: "generated_answer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "citations", as: "answer_citations", type: "string" },
          { field: "data_freshness", as: "data_freshness", type: "string" },
          {
            field: "needs_reassessment",
            as: "needs_reassessment",
            type: "boolean",
          },
        ],
      ),
      n("5", "condition", "Is confidence below 0.7 or data stale?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "answer_confidence", operator: "less_than", value: "0.7" },
            { field: "data_freshness", operator: "equals", value: "stale" },
          ],
          logic: "or",
        },
      }),
      slackNode(
        "6",
        "Escalate to vendor security team for expert review",
        1240,
        ":lock: *Vendor Security Question — Expert Review Needed*\n\nVendor: {vendor_name}\nQuestion: {question_text}\nCategory: {question_category}\nAsked by: {asker_email}\nUrgency: {question_urgency}\n\nBot answer (confidence: {answer_confidence}):\n{generated_answer}\n\nData freshness: {data_freshness}\nCurrent risk level: {vendor_risk_level}\nNeeds reassessment: {needs_reassessment}\n\nPlease provide a verified response or initiate a new vendor assessment.",
      ),
      n("7", "hitApi", "Create new assessment ticket in Jira if needed", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://company.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_credentials}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project": {"key": "VSEC"}, "summary": "Vendor Assessment: {vendor_name} — {question_category}", "issuetype": {"name": "Task"}, "priority": {"name": "Medium"}, "description": {"type": "doc", "version": 1, "content": [{"type": "paragraph", "content": [{"type": "text", "text": "Question: {question_text}. Requester: {asker_email}. Current data freshness: {data_freshness}. Risk level: {vendor_risk_level}. Bot confidence: {answer_confidence}. Reassessment needed: {needs_reassessment}."}]}]}, "labels": ["vendor-assessment", "auto-created"]}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Log inquiry, response, and reviewer involvement",
        1720,
        "VendorSecurityInquiries",
        '[["{vendor_name}", "{question_category}", "{question_text}", "{asker_email}", "{answer_confidence}", "{data_freshness}", "{vendor_risk_level}", "{needs_reassessment}", "{jira_ticket_key}", "=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Self-service vendor security knowledge bot: parses questions from procurement/engineering, looks up existing vendor assessments, generates grounded answers with confidence scoring, escalates low-confidence or stale-data queries to the security team via Slack, creates Jira tickets for new assessments, and logs all inquiries.",
  },

  // ─────────────────────────────────────────────────────────
  // 17. Vulnerability Disclosure Program Bot
  // ─────────────────────────────────────────────────────────
  "Vulnerability Disclosure Program Bot": {
    nodes: [
      trigger("Researcher submits vulnerability report", "webhook_form", {
        source: "vulnerability-disclosure",
        event: "report.submitted",
        howItStarts:
          "Fires when a security researcher submits a vulnerability report through the public disclosure program portal.",
        setup:
          "Configure the vulnerability disclosure portal (HackerOne, Bugcrowd, or custom) to POST reports to this webhook URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract vulnerability details and validate completeness",
        280,
        "You are a vulnerability disclosure triage specialist. Extract all vulnerability details from the report, validate completeness against required fields, and check if the report is within program scope.",
        "Parse this vulnerability report: {webhook_payload}. Return JSON with: reporter_handle (string), reporter_email (string), vuln_title (string), vuln_description (string), vuln_type (one of: xss, sqli, rce, ssrf, idor, authentication_bypass, information_disclosure, csrf, open_redirect, other), affected_component (string), reproduction_steps (string), impact_description (string), proof_of_concept (string), in_scope (boolean, true if the affected component is a production system), is_complete (boolean, true if all required fields are present), missing_fields (array of strings), suggested_severity (critical/high/medium/low/informational).",
        [
          { field: "reporter_handle", as: "reporter_handle", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          { field: "vuln_title", as: "vuln_title", type: "string" },
          { field: "vuln_type", as: "vuln_type", type: "string" },
          {
            field: "affected_component",
            as: "affected_component",
            type: "string",
          },
          {
            field: "impact_description",
            as: "impact_description",
            type: "string",
          },
          { field: "in_scope", as: "in_scope", type: "boolean" },
          { field: "is_complete", as: "is_complete", type: "boolean" },
          {
            field: "suggested_severity",
            as: "suggested_severity",
            type: "string",
          },
        ],
      ),
      emailNode(
        "3",
        "Send immediate acknowledgment to researcher with case ID",
        520,
        "{reporter_email}",
        "Vulnerability Report Received — Case #{case_id}: {vuln_title}",
        "Dear {reporter_handle},\n\nThank you for submitting a vulnerability report to our disclosure program.\n\nCase ID: #{case_id}\nReport Title: {vuln_title}\nStatus: Received and Under Review\n\nWhat happens next:\n1. Our security team will triage your report within 2 business days\n2. You will receive a severity assessment and initial response within 5 business days\n3. If eligible, bounty determination will follow verification\n\nPlease reference Case #{case_id} in all future communications about this report.\n\nPlease do not publicly disclose this vulnerability until we have had a chance to verify and remediate it.\n\nThank you for helping us improve our security.\n\nSecurity Team",
      ),
      aiNode(
        "4",
        "Classify vulnerability type, severity, and affected component",
        760,
        "You are a senior security engineer. Perform detailed triage of the vulnerability report. Classify the vulnerability, assess CVSS-based severity, determine the affected product/component owner, and evaluate bounty eligibility.",
        "Vulnerability: {vuln_title}. Type: {vuln_type}. Component: {affected_component}. Impact: {impact_description}. Suggested severity: {suggested_severity}. In scope: {in_scope}. Return JSON with: confirmed_severity (critical/high/medium/low/informational), cvss_estimate (number 0-10), component_owner_team (string like platform-team, api-team, frontend-team, infrastructure-team), requires_immediate_action (boolean), bounty_eligible (boolean, true if in_scope and severity >= medium), estimated_bounty_range (string like $500-$1000), duplicate_likelihood (low/medium/high), remediation_complexity (simple/moderate/complex).",
        [
          {
            field: "confirmed_severity",
            as: "confirmed_severity",
            type: "string",
          },
          { field: "cvss_estimate", as: "cvss_estimate", type: "number" },
          {
            field: "component_owner_team",
            as: "component_owner_team",
            type: "string",
          },
          { field: "bounty_eligible", as: "bounty_eligible", type: "boolean" },
          {
            field: "estimated_bounty_range",
            as: "estimated_bounty_range",
            type: "string",
          },
          {
            field: "remediation_complexity",
            as: "remediation_complexity",
            type: "string",
          },
        ],
      ),
      n(
        "5",
        "runCode",
        "Assign to product/security owner based on component",
        1000,
        {
          runCodeDetails: {
            code: `const team = '{component_owner_team}'; const severity = '{confirmed_severity}'; const teamMap = { 'platform-team': { lead: 'platform-lead@company.com', channel: '#platform-security' }, 'api-team': { lead: 'api-lead@company.com', channel: '#api-security' }, 'frontend-team': { lead: 'frontend-lead@company.com', channel: '#frontend-security' }, 'infrastructure-team': { lead: 'infra-lead@company.com', channel: '#infra-security' } }; const assigned = teamMap[team] || teamMap['platform-team']; const sla = severity === 'critical' ? '24 hours' : severity === 'high' ? '72 hours' : severity === 'medium' ? '7 days' : '30 days'; return { assigned_lead: assigned.lead, assigned_channel: assigned.channel, remediation_sla: sla, is_high_severity: severity === 'critical' || severity === 'high' };`,
            outputs: [
              { name: "assigned_lead", type: "string" },
              { name: "assigned_channel", type: "string" },
              { name: "remediation_sla", type: "string" },
              { name: "is_high_severity", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Create verification and remediation ticket in Jira",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://company.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_credentials}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project": {"key": "VDP"}, "summary": "VDP-{case_id}: {vuln_title}", "issuetype": {"name": "Bug"}, "priority": {"name": "{confirmed_severity}"}, "assignee": {"accountId": "{assigned_lead_jira_id}"}, "description": {"type": "doc", "version": 1, "content": [{"type": "paragraph", "content": [{"type": "text", "text": "Reporter: {reporter_handle}. Type: {vuln_type}. Component: {affected_component}. CVSS: {cvss_estimate}. Severity: {confirmed_severity}. Impact: {impact_description}. SLA: {remediation_sla}. Bounty eligible: {bounty_eligible}. Est. bounty: {estimated_bounty_range}. Complexity: {remediation_complexity}."}]}]}, "labels": ["vdp", "security", "{vuln_type}", "{confirmed_severity}"]}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
      ),
      n("7", "condition", "Is severity high+ and bounty eligible?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "is_high_severity", operator: "equals", value: "true" },
            { field: "bounty_eligible", operator: "equals", value: "true" },
          ],
          logic: "and",
        },
      }),
      n("8", "approvalGate", "Security/finance approve bounty amount", 1720, {
        approvalDetails: {
          approver: "vdp-bounty-approvers",
          timeout: 72,
          timeoutUnit: "hours",
          message:
            "Bounty Approval Request\nCase: #{case_id} — {vuln_title}\nReporter: {reporter_handle}\nSeverity: {confirmed_severity} (CVSS: {cvss_estimate})\nType: {vuln_type}\nComponent: {affected_component}\nEstimated bounty: {estimated_bounty_range}\nJira: {jira_ticket_key}\n\nPlease approve the bounty amount for this verified vulnerability.",
        },
      }),
      sheetsNode(
        "9",
        "Track reporter communication and advisory status",
        1960,
        "VDPTracker",
        '[["{case_id}", "{reporter_handle}", "{reporter_email}", "{vuln_title}", "{vuln_type}", "{confirmed_severity}", "{cvss_estimate}", "{affected_component}", "{component_owner_team}", "{bounty_eligible}", "{estimated_bounty_range}", "{jira_ticket_key}", "{remediation_sla}", "=NOW()", "triaged"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
    summary:
      "Complete vulnerability disclosure program automation: validates and triages researcher reports, sends immediate acknowledgment with case ID, classifies severity with CVSS scoring, assigns to component owners, creates Jira verification tickets, routes bounty-eligible high-severity findings through approval, and tracks all communication milestones.",
  },

  // ─────────────────────────────────────────────────────────
  // 18. After Hours Support Bot
  // ─────────────────────────────────────────────────────────
  "After Hours Support Bot": {
    nodes: [
      trigger("Customer message received", "webhook_app", {
        source: "support-channel",
        event: "message.received",
        howItStarts:
          "Fires when a customer sends a message through any support channel (chat, email, web form) to determine if it is within or outside business hours.",
        setup:
          "Connect your support channels to POST customer messages to this webhook. Define business hours and timezone in the runCode node.",
        inputFields: [],
      }),
      n(
        "2",
        "runCode",
        "Check if current time is within business hours by timezone",
        280,
        {
          runCodeDetails: {
            code: `const now = new Date(); const businessHours = { start: 9, end: 18 }; const customerTz = '{customer_timezone}' || 'America/New_York'; const options = { timeZone: customerTz, hour: 'numeric', hour12: false }; const localHour = parseInt(new Intl.DateTimeFormat('en-US', options).format(now)); const dayOfWeek = new Intl.DateTimeFormat('en-US', { timeZone: customerTz, weekday: 'short' }).format(now); const isWeekend = dayOfWeek === 'Sat' || dayOfWeek === 'Sun'; const isBusinessHours = !isWeekend && localHour >= businessHours.start && localHour < businessHours.end; const nextOpen = isBusinessHours ? 'now' : isWeekend ? 'next Monday at 9 AM' : localHour < businessHours.start ? 'today at 9 AM' : 'tomorrow at 9 AM'; return { is_business_hours: isBusinessHours, local_hour: localHour, day_of_week: dayOfWeek, next_available: nextOpen };`,
            outputs: [
              { name: "is_business_hours", type: "boolean" },
              { name: "local_hour", type: "number" },
              { name: "day_of_week", type: "string" },
              { name: "next_available", type: "string" },
            ],
          },
        },
      ),
      n("3", "condition", "Is it within business hours?", 520, {
        conditionDetails: {
          conditions: [
            { field: "is_business_hours", operator: "equals", value: "true" },
          ],
        },
      }),
      aiNode(
        "4",
        "Classify intent and urgency from customer message",
        760,
        "You are a support message classifier. Analyze the customer's message to determine intent, urgency, and whether this requires immediate attention even outside business hours.",
        "Classify this customer support message received outside business hours: {webhook_payload}. Return JSON with: customer_email (string), customer_name (string if available), message_text (string), intent (one of: billing, technical, account, general_inquiry, urgent_issue, feedback), urgency (low/medium/high/critical), sentiment (positive/neutral/negative/frustrated), requires_immediate_attention (boolean, true only for service outages, security issues, or payment failures), summary (brief string summarizing the issue).",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "intent", as: "message_intent", type: "string" },
          { field: "urgency", as: "message_urgency", type: "string" },
          { field: "summary", as: "message_summary", type: "string" },
        ],
      ),
      n(
        "5",
        "hitApi",
        "Search knowledge base for relevant answers via Pinecone",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_host}/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{message_embedding}" },
              { key: "topK", value: "5" },
              { key: "namespace", value: "support-kb" },
              { key: "includeMetadata", value: "true" },
            ],
            variables: [
              { name: "matches", customName: "kb_matches", type: "string" },
              {
                name: "matches[0].score",
                customName: "top_match_score",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "6",
        "Generate grounded response with confidence",
        1240,
        "You are an empathetic after-hours support assistant. Generate a helpful response acknowledging it is outside business hours. If knowledge base articles address the issue, provide the answer. Otherwise, set appropriate expectations for follow-up.",
        "Customer: {customer_name}. Issue: {message_text}. Intent: {message_intent}. Urgency: {message_urgency}. KB matches: {kb_matches}. Next available agent: {next_available}. Generate JSON with: response (string, empathetic after-hours response with solution if available or expectation-setting if not), confidence (number 0-1, how well the KB matches address the issue), includes_solution (boolean), follow_up_needed (boolean).",
        [
          { field: "response", as: "auto_response", type: "string" },
          { field: "confidence", as: "response_confidence", type: "number" },
          {
            field: "includes_solution",
            as: "includes_solution",
            type: "boolean",
          },
          {
            field: "follow_up_needed",
            as: "follow_up_needed",
            type: "boolean",
          },
        ],
      ),
      n("7", "condition", "Is response confidence above 0.8?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "response_confidence",
              operator: "greater_than",
              value: "0.8",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Send escalation summary for next business day review",
        1720,
        "{support_team_email}",
        "After-Hours Support — Follow-up Needed: {message_summary}",
        "After-Hours Customer Message Requiring Follow-up\n\nCustomer: {customer_name} ({customer_email})\nReceived: {day_of_week} at {local_hour}:00 (outside business hours)\nIntent: {message_intent} | Urgency: {message_urgency}\n\nMessage: {message_text}\n\nAuto-response sent: {auto_response}\nResponse confidence: {response_confidence}\nSolution included: {includes_solution}\n\nPlease follow up with the customer when business hours resume ({next_available}).\n\nAfter-Hours Support Bot",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "8", "output_1"),
      e("3", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Intelligent after-hours support bot: checks business hours by timezone, classifies message intent and urgency with AI, searches knowledge base for solutions, generates empathetic responses with confidence scoring, and escalates unresolved issues to the support team for next-business-day follow-up.",
  },

  // ─────────────────────────────────────────────────────────
  // 19. Attentive Two-Way SMS Conversation
  // ─────────────────────────────────────────────────────────
  "Attentive Two-Way SMS Conversation": {
    nodes: [
      trigger("Inbound SMS received via Attentive", "webhook_app", {
        source: "attentive",
        event: "sms.inbound",
        howItStarts:
          "Fires when a customer sends an inbound SMS message via the Attentive platform.",
        setup:
          "Configure Attentive webhook to POST inbound SMS messages to this URL. Ensure HubSpot CRM access for customer context.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract message intent and check for opt-out keywords",
        280,
        "You are an SMS message analyzer for a commerce brand. Extract the customer's intent, phone number, and check for any opt-out keywords (STOP, UNSUBSCRIBE, CANCEL, QUIT, END). Also detect any compliance-relevant language.",
        "Analyze this inbound SMS webhook: {webhook_payload}. Return JSON with: phone_number (string), message_text (string), is_opt_out (boolean, true if message contains STOP/UNSUBSCRIBE/CANCEL/QUIT/END), opt_out_keyword (string if found, empty otherwise), intent (one of: order_status, support, feedback, opt_out, product_question, reorder, other), sentiment (positive/neutral/negative).",
        [
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "is_opt_out", as: "is_opt_out", type: "boolean" },
          { field: "intent", as: "sms_intent", type: "string" },
          { field: "sentiment", as: "customer_sentiment", type: "string" },
        ],
      ),
      n("3", "runCode", "Verify TCPA compliance state and consent", 520, {
        runCodeDetails: {
          code: `const isOptOut = {is_opt_out}; const phone = '{phone_number}'; const consentRecord = true; const state = 'active'; const quietHoursStart = 21; const quietHoursEnd = 8; const currentHour = new Date().getHours(); const inQuietHours = currentHour >= quietHoursStart || currentHour < quietHoursEnd; const canRespond = !isOptOut && consentRecord && !inQuietHours; return { tcpa_compliant: canRespond, has_consent: consentRecord, consent_state: isOptOut ? 'opt_out_requested' : state, in_quiet_hours: inQuietHours, can_send_response: canRespond, block_reason: isOptOut ? 'opt_out' : !consentRecord ? 'no_consent' : inQuietHours ? 'quiet_hours' : 'none' };`,
          outputs: [
            { name: "tcpa_compliant", type: "boolean" },
            { name: "consent_state", type: "string" },
            { name: "can_send_response", type: "boolean" },
            { name: "block_reason", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Valid consent and not opt-out?", 760, {
        conditionDetails: {
          conditions: [
            { field: "can_send_response", operator: "equals", value: "true" },
          ],
        },
      }),
      aiNode(
        "5",
        "Classify intent with CRM context from HubSpot",
        1000,
        "You are a customer service SMS specialist. Using the customer's message intent and any available CRM context, determine the best response strategy.",
        "Customer phone: {phone_number}. Message: {message_text}. Intent: {sms_intent}. Sentiment: {customer_sentiment}. CRM context: {crm_context}. Classify and return JSON with: response_strategy (one of: provide_order_status, answer_question, collect_feedback, route_to_agent, send_reorder_link), crm_data_needed (array of strings), personalization_context (string with relevant CRM details like name, last order, loyalty status).",
        [
          {
            field: "response_strategy",
            as: "response_strategy",
            type: "string",
          },
          {
            field: "personalization_context",
            as: "personalization_context",
            type: "string",
          },
        ],
      ),
      aiNode(
        "6",
        "Generate 160-char response with personalization",
        1240,
        "You are an SMS copywriter for a commerce brand. Generate a response within 160 characters that is friendly, helpful, and personalized. Must be under 160 characters including any URLs. Do NOT include opt-out language unless specifically an opt-out confirmation.",
        "Intent: {sms_intent}. Strategy: {response_strategy}. Customer context: {personalization_context}. Message: {message_text}. Generate JSON with: sms_response (string, max 160 chars), char_count (number), includes_link (boolean), response_type (automated/needs_agent).",
        [
          { field: "sms_response", as: "sms_response", type: "string" },
          { field: "char_count", as: "char_count", type: "number" },
          { field: "response_type", as: "response_type", type: "string" },
        ],
      ),
      smsNode(
        "7",
        "Send SMS response via Attentive API",
        1480,
        "{phone_number}",
        "{sms_response}",
      ),
      sheetsNode(
        "8",
        "Log conversation with compliance state",
        1720,
        "SMSConversationLog",
        '[["{phone_number}", "{message_text}", "{sms_intent}", "{customer_sentiment}", "{consent_state}", "{can_send_response}", "{sms_response}", "{response_strategy}", "{response_type}", "=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "TCPA-compliant two-way SMS conversation bot via Attentive: extracts message intent and opt-out keywords, verifies consent and quiet hours, classifies intent with HubSpot CRM context, generates personalized 160-character responses, sends via SMS, and logs all conversations with compliance metadata.",
  },

  // ─────────────────────────────────────────────────────────
  // 20. Auto Responder Workflow
  // ─────────────────────────────────────────────────────────
  "Auto Responder Workflow": {
    nodes: [
      trigger("Inbound customer email or message", "webhook_app", {
        source: "support-inbox",
        event: "message.received",
        howItStarts:
          "Fires when a customer email or message arrives in the support inbox.",
        setup:
          "Connect your email provider or helpdesk (Gmail, Zendesk, Intercom) to POST inbound messages to this webhook.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Classify message intent, urgency, and topic category",
        280,
        "You are a support message classifier. Analyze the incoming customer message and classify it by intent, urgency, and topic to enable automatic response selection.",
        "Classify this inbound customer message: {webhook_payload}. Return JSON with: customer_email (string), customer_name (string if available), subject (string), message_body (string), intent (one of: question, complaint, feature_request, billing, account, technical, shipping, return, praise, spam), urgency (low/medium/high), topic_category (string, specific topic like password_reset, order_tracking, refund_request, pricing_question, etc.), sentiment (positive/neutral/negative/angry), language (string, ISO code).",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "subject", as: "subject", type: "string" },
          { field: "message_body", as: "message_body", type: "string" },
          { field: "intent", as: "message_intent", type: "string" },
          { field: "topic_category", as: "topic_category", type: "string" },
          { field: "urgency", as: "message_urgency", type: "string" },
          { field: "sentiment", as: "customer_sentiment", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Select best response template by category and urgency",
        520,
        {
          runCodeDetails: {
            code: `const intent = '{message_intent}'; const topic = '{topic_category}'; const urgency = '{message_urgency}'; const templates = { password_reset: { id: 'T001', name: 'Password Reset Instructions', confidence: 0.95, body: 'instructions for resetting password' }, order_tracking: { id: 'T002', name: 'Order Tracking Help', confidence: 0.92, body: 'how to track your order' }, refund_request: { id: 'T003', name: 'Refund Process', confidence: 0.88, body: 'refund policy and process' }, pricing_question: { id: 'T004', name: 'Pricing Information', confidence: 0.90, body: 'pricing details and plans' }, billing: { id: 'T005', name: 'Billing Support', confidence: 0.87, body: 'billing issue resolution' } }; const template = templates[topic] || { id: 'T999', name: 'General Acknowledgment', confidence: 0.70, body: 'general acknowledgment and routing' }; const adjustedConfidence = urgency === 'high' ? Math.max(template.confidence - 0.1, 0) : template.confidence; return { template_id: template.id, template_name: template.name, template_confidence: adjustedConfidence, template_body: template.body, is_confident: adjustedConfidence > 0.85 };`,
            outputs: [
              { name: "template_id", type: "string" },
              { name: "template_name", type: "string" },
              { name: "template_confidence", type: "number" },
              { name: "template_body", type: "string" },
              { name: "is_confident", type: "boolean" },
            ],
          },
        },
      ),
      n("4", "condition", "Is template confidence above 0.85?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "template_confidence",
              operator: "greater_than",
              value: "0.85",
            },
          ],
        },
      }),
      aiNode(
        "5",
        "Personalize template response within approved copy boundaries",
        1000,
        "You are a customer support email writer. Personalize the selected response template with the customer's specific details while staying within the approved copy boundaries. Do not promise anything not in the template. Be warm, professional, and concise.",
        "Customer: {customer_name}. Email: {customer_email}. Subject: {subject}. Their message: {message_body}. Intent: {message_intent}. Topic: {topic_category}. Sentiment: {customer_sentiment}. Template: {template_name} — {template_body}. Generate JSON with: personalized_response (string, full email body personalized for this customer), subject_line (string, reply subject), tone_appropriate (boolean, true if response tone matches customer sentiment appropriately).",
        [
          {
            field: "personalized_response",
            as: "personalized_response",
            type: "string",
          },
          { field: "subject_line", as: "reply_subject", type: "string" },
        ],
      ),
      emailNode(
        "6",
        "Send auto-response to customer",
        1240,
        "{customer_email}",
        "Re: {reply_subject}",
        "{personalized_response}",
      ),
      sheetsNode(
        "7",
        "Log auto-response with template used and confidence",
        1480,
        "AutoResponderLog",
        '[["{customer_email}", "{customer_name}", "{message_intent}", "{topic_category}", "{message_urgency}", "{customer_sentiment}", "{template_id}", "{template_name}", "{template_confidence}", "=NOW()", "auto_responded"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary:
      "Intelligent auto-responder for customer support: classifies inbound messages by intent, urgency, and topic, selects the best response template with confidence scoring, personalizes responses using AI while staying within approved copy, sends auto-replies, and logs all interactions for quality analysis.",
  },

  // ─────────────────────────────────────────────────────────
  // 21. FAQ Chatbot
  // ─────────────────────────────────────────────────────────
  "FAQ Chatbot": {
    nodes: [
      trigger("User asks question via chat widget", "webhook_app", {
        source: "chat-widget",
        event: "message.received",
        howItStarts:
          "Fires when a user submits a question through the website chat widget or messaging interface.",
        setup:
          "Connect your chat widget (Intercom, Drift, custom) to POST messages to this webhook. Set up Pinecone FAQ index.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract question intent and classify topic",
        280,
        "You are a FAQ query analyzer. Extract the user's actual question, classify its topic, and identify the best search terms for knowledge base retrieval.",
        "Parse this chat message: {webhook_payload}. Return JSON with: user_id (string), user_name (string if available), question_text (string, the actual question), topic (one of: product, pricing, billing, technical, account, shipping, returns, integrations, security, general), search_terms (array of key phrases for KB search), is_followup (boolean, true if references previous conversation).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "question_text", as: "question_text", type: "string" },
          { field: "topic", as: "question_topic", type: "string" },
          { field: "search_terms", as: "search_terms", type: "string" },
        ],
      ),
      n("3", "hitApi", "Vector search FAQ knowledge base in Pinecone", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_host}/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{question_embedding}" },
            { key: "topK", value: "5" },
            { key: "namespace", value: "faq-articles" },
            { key: "includeMetadata", value: "true" },
            { key: "filter", value: '{"topic": {"$eq": "{question_topic}"}}' },
          ],
          variables: [
            { name: "matches", customName: "faq_matches", type: "string" },
            {
              name: "matches[0].score",
              customName: "top_match_score",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "Generate grounded answer with source citations and confidence",
        760,
        "You are a helpful FAQ chatbot. Generate a clear, concise answer based ONLY on the retrieved FAQ articles. Include source citations. If the articles do not adequately address the question, acknowledge this and set low confidence.",
        "User question: {question_text}. Topic: {question_topic}. FAQ articles found: {faq_matches}. Generate JSON with: answer (string, clear and helpful response), confidence (number 0-1), sources (array of objects with article_title and article_url), is_complete_answer (boolean, true if the FAQ fully addresses the question), suggested_followups (array of related question strings).",
        [
          { field: "answer", as: "faq_answer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "sources", as: "answer_sources", type: "string" },
          {
            field: "is_complete_answer",
            as: "is_complete_answer",
            type: "boolean",
          },
          {
            field: "suggested_followups",
            as: "suggested_followups",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Is answer confidence above 0.75?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "answer_confidence",
              operator: "greater_than",
              value: "0.75",
            },
          ],
        },
      }),
      n("6", "sendMessage", "Reply with answer and source links", 1240, {
        sendMessageDetails: {
          channel: "chat",
          message:
            "{faq_answer}\n\nSources: {answer_sources}\n\nWas this helpful? You might also want to know:\n{suggested_followups}",
        },
      }),
      slackNode(
        "7",
        "Route unanswered question to support agent with context",
        1240,
        ":question: *FAQ Bot — Agent Needed*\n\nUser: {user_name} ({user_id})\nTopic: {question_topic}\nQuestion: {question_text}\n\nBot answer (confidence: {answer_confidence}):\n{faq_answer}\n\nSources checked: {answer_sources}\nComplete answer: {is_complete_answer}\n\nPlease respond to this customer in the chat widget.",
        480,
      ),
      sheetsNode(
        "8",
        "Log query, answer, and confidence for improvement",
        1480,
        "FAQBotLog",
        '[["{user_id}", "{question_topic}", "{question_text}", "{answer_confidence}", "{is_complete_answer}", "{answer_sources}", "=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "AI-powered FAQ chatbot: classifies user questions by topic, searches Pinecone FAQ knowledge base with semantic search, generates grounded answers with citations and confidence scoring, sends high-confidence replies directly to chat, routes low-confidence queries to human agents via Slack, and logs all interactions for knowledge base improvement.",
  },

  // ─────────────────────────────────────────────────────────
  // 22. IT Support Ticket Bot
  // ─────────────────────────────────────────────────────────
  "IT Support Ticket Bot": {
    nodes: [
      trigger(
        "Employee submits IT issue via Slack or web form",
        "webhook_app",
        {
          source: "it-support",
          event: "issue.submitted",
          howItStarts:
            "Fires when an employee submits an IT support request through the Slack command, web form, or helpdesk portal.",
          setup:
            "Configure Slack slash command or web form to POST IT issues to this webhook. Set up Jira project for IT tickets.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "Classify issue type: access, hardware, software, network, security",
        280,
        "You are an IT support triage specialist. Analyze the employee's issue description and classify it by type, urgency, and potential self-service eligibility.",
        "Classify this IT support request: {webhook_payload}. Return JSON with: employee_email (string), employee_name (string), issue_description (string), issue_type (one of: access_request, hardware, software, network, security, email, printing, vpn, phone, other), urgency (low/medium/high/critical), affected_system (string), is_outage (boolean, true if multiple users affected), employee_department (string if available).",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          {
            field: "issue_description",
            as: "issue_description",
            type: "string",
          },
          { field: "issue_type", as: "issue_type", type: "string" },
          { field: "urgency", as: "issue_urgency", type: "string" },
          { field: "affected_system", as: "affected_system", type: "string" },
          { field: "is_outage", as: "is_outage", type: "boolean" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Determine self-service eligibility by issue type",
        520,
        {
          runCodeDetails: {
            code: `const type = '{issue_type}'; const urgency = '{issue_urgency}'; const selfServiceTypes = { software: { eligible: true, guide: 'software_installation' }, vpn: { eligible: true, guide: 'vpn_troubleshooting' }, email: { eligible: true, guide: 'email_setup' }, printing: { eligible: true, guide: 'printer_troubleshooting' }, network: { eligible: urgency !== 'critical', guide: 'network_troubleshooting' } }; const ss = selfServiceTypes[type]; const eligible = ss ? ss.eligible : false; const guideName = ss ? ss.guide : 'none'; const priority = urgency === 'critical' ? 'Highest' : urgency === 'high' ? 'High' : urgency === 'medium' ? 'Medium' : 'Low'; return { self_service_eligible: eligible, troubleshooting_guide: guideName, jira_priority: priority, needs_ticket: !eligible || urgency === 'critical' || urgency === 'high' };`,
            outputs: [
              { name: "self_service_eligible", type: "boolean" },
              { name: "troubleshooting_guide", type: "string" },
              { name: "jira_priority", type: "string" },
              { name: "needs_ticket", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is the issue eligible for self-service resolution?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "self_service_eligible",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
      aiNode(
        "5",
        "Generate step-by-step troubleshooting guide",
        1000,
        "You are an IT support specialist. Generate a clear, step-by-step troubleshooting guide for the employee's specific issue. Include screenshots descriptions where helpful. Be patient and thorough.",
        "Issue: {issue_description}. Type: {issue_type}. System: {affected_system}. Guide category: {troubleshooting_guide}. Generate JSON with: steps (array of strings, each a clear step), estimated_time (string like 5-10 minutes), escalation_trigger (string describing when to escalate if self-service fails), success_indicator (string describing how they know it is fixed).",
        [
          { field: "steps", as: "troubleshooting_steps", type: "string" },
          { field: "estimated_time", as: "estimated_time", type: "string" },
          {
            field: "escalation_trigger",
            as: "escalation_trigger",
            type: "string",
          },
        ],
      ),
      n(
        "6",
        "hitApi",
        "Create Jira ticket for non-self-service issues",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://company.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_credentials}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project": {"key": "ITSUP"}, "summary": "IT Support: {issue_type} — {affected_system}", "issuetype": {"name": "Service Request"}, "priority": {"name": "{jira_priority}"}, "description": {"type": "doc", "version": 1, "content": [{"type": "paragraph", "content": [{"type": "text", "text": "Reporter: {employee_name} ({employee_email}). Department: {employee_department}. Issue: {issue_description}. Type: {issue_type}. Urgency: {issue_urgency}. Affected system: {affected_system}. Is outage: {is_outage}."}]}]}, "labels": ["it-support", "{issue_type}", "auto-created"]}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_ticket_key", type: "string" },
              { name: "self", customName: "jira_ticket_url", type: "string" },
            ],
          },
        },
        480,
      ),
      slackNode(
        "7",
        "Notify IT team with ticket details and context",
        1240,
        ":computer: *New IT Support Ticket*\n\nTicket: {jira_ticket_key}\nEmployee: {employee_name} ({employee_email})\nType: {issue_type} | Priority: {jira_priority}\nSystem: {affected_system}\nOutage: {is_outage}\n\nDescription: {issue_description}\n\nSelf-service eligible: {self_service_eligible}\nPlease assign and begin triage.",
      ),
      sheetsNode(
        "8",
        "Log resolution path and time",
        1480,
        "ITSupportLog",
        '[["{employee_email}", "{employee_name}", "{issue_type}", "{issue_urgency}", "{affected_system}", "{is_outage}", "{self_service_eligible}", "{jira_ticket_key}", "=NOW()", "open"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Smart IT support ticket bot: classifies employee issues by type and urgency, determines self-service eligibility, generates step-by-step troubleshooting guides for eligible issues, creates Jira tickets for complex problems, notifies IT team via Slack with context, and logs resolution paths.",
  },

  // ─────────────────────────────────────────────────────────
  // 23. Internal IT Helpdesk Bot
  // ─────────────────────────────────────────────────────────
  "Internal IT Helpdesk Bot": {
    nodes: [
      trigger("Employee message in IT helpdesk channel", "webhook_app", {
        source: "slack-helpdesk",
        event: "message.posted",
        howItStarts:
          "Fires when an employee posts a message in the #it-helpdesk Slack channel or DMs the IT helpdesk bot.",
        setup:
          "Install the Slack bot in the #it-helpdesk channel. Configure Jira and approval workflow routing.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Classify request type and determine approval need",
        280,
        "You are an IT helpdesk classifier. Analyze the employee's request and classify it by type. Determine if the request requires management approval (software installs, access requests) or can be handled immediately.",
        "Classify this IT helpdesk message: {webhook_payload}. Return JSON with: employee_name (string), employee_email (string), employee_slack_id (string), request_text (string), request_type (one of: software_install, access_request, password_reset, hardware_issue, vpn_issue, email_issue, other), requires_approval (boolean, true for software_install and access_request), target_system (string, the system or software being requested), justification (string if provided), urgency (low/medium/high).",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          {
            field: "employee_slack_id",
            as: "employee_slack_id",
            type: "string",
          },
          { field: "request_text", as: "request_text", type: "string" },
          { field: "request_type", as: "request_type", type: "string" },
          {
            field: "requires_approval",
            as: "requires_approval",
            type: "boolean",
          },
          { field: "target_system", as: "target_system", type: "string" },
          { field: "urgency", as: "request_urgency", type: "string" },
        ],
      ),
      n("3", "condition", "Does this request require manager approval?", 520, {
        conditionDetails: {
          conditions: [
            { field: "requires_approval", operator: "equals", value: "true" },
          ],
        },
      }),
      n(
        "4",
        "approvalGate",
        "IT manager approves software install or access request",
        760,
        {
          approvalDetails: {
            approver: "it-manager",
            timeout: 24,
            timeoutUnit: "hours",
            message:
              "IT Approval Request\nEmployee: {employee_name} ({employee_email})\nRequest: {request_type}\nTarget: {target_system}\nJustification: {request_text}\nUrgency: {request_urgency}\n\nPlease approve or deny this {request_type} for {target_system}.",
          },
        },
      ),
      n(
        "5",
        "hitApi",
        "Create or update Jira ticket with request details",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://company.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_credentials}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project": {"key": "ITHD"}, "summary": "Helpdesk: {request_type} — {target_system} for {employee_name}", "issuetype": {"name": "Service Request"}, "priority": {"name": "Medium"}, "description": {"type": "doc", "version": 1, "content": [{"type": "paragraph", "content": [{"type": "text", "text": "Employee: {employee_name} ({employee_email}). Request: {request_text}. Type: {request_type}. Target: {target_system}. Approval required: {requires_approval}. Urgency: {request_urgency}."}]}]}, "labels": ["helpdesk", "{request_type}"]}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "6",
        "Generate status update and next steps for employee",
        1240,
        "You are a helpful IT helpdesk assistant. Generate a friendly status update for the employee about their request, including what happens next and estimated timeline.",
        "Employee: {employee_name}. Request: {request_text}. Type: {request_type}. Target: {target_system}. Requires approval: {requires_approval}. Urgency: {request_urgency}. Jira ticket: {jira_ticket_key}. Generate JSON with: status_message (string, friendly update including ticket number and next steps), estimated_resolution (string like 2-4 hours or 1-2 business days), next_action (string describing what happens next).",
        [
          { field: "status_message", as: "status_message", type: "string" },
          {
            field: "estimated_resolution",
            as: "estimated_resolution",
            type: "string",
          },
        ],
      ),
      n("7", "hitApi", "DM employee in Slack with ticket status", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{employee_slack_id}" },
            {
              key: "text",
              value:
                ":ticket: {status_message}\n\nTicket: {jira_ticket_key}\nEstimated resolution: {estimated_resolution}\n\nYou can check your ticket status anytime by typing `/it-status {jira_ticket_key}`",
            },
          ],
          variables: [{ name: "ok", customName: "dm_sent", type: "boolean" }],
        },
      }),
      sheetsNode(
        "8",
        "Track request lifecycle and SLA compliance",
        1720,
        "HelpdeskTracker",
        '[["{employee_name}", "{employee_email}", "{request_type}", "{target_system}", "{requires_approval}", "{request_urgency}", "{jira_ticket_key}", "{estimated_resolution}", "=NOW()", "open"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Internal IT helpdesk bot: classifies employee requests, routes software installs and access requests through manager approval, creates Jira tickets, generates friendly status updates with next steps, DMs employees in Slack with ticket info, and tracks SLA compliance across the request lifecycle.",
  },

  // ─────────────────────────────────────────────────────────
  // 24. Knowledge Base Search Bot
  // ─────────────────────────────────────────────────────────
  "Knowledge Base Search Bot": {
    nodes: [
      trigger("User searches knowledge base", "webhook_app", {
        source: "kb-search",
        event: "search.query",
        howItStarts:
          "Fires when a user performs a search query in the knowledge base portal or sends a question via the search widget.",
        setup:
          "Connect the knowledge base search widget to POST queries to this webhook. Set up Pinecone with KB articles, FAQs, and guides.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Extract search intent and key terms",
        280,
        "You are a search query analyzer. Parse the user's search query to extract the core intent, key terms, and any filters that should be applied to improve search results.",
        "Analyze this knowledge base search: {webhook_payload}. Return JSON with: user_id (string), query_text (string, the search query), intent (one of: how_to, troubleshooting, reference, definition, comparison, setup), key_terms (array of important phrases), content_type_filter (one of: article, faq, guide, tutorial, api_docs, all), product_filter (string, specific product if mentioned, otherwise all).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "query_text", as: "query_text", type: "string" },
          { field: "intent", as: "search_intent", type: "string" },
          { field: "key_terms", as: "key_terms", type: "string" },
          { field: "content_type_filter", as: "content_type", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Semantic search across KB articles, FAQs, and guides via Pinecone",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_host}/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{query_embedding}" },
              { key: "topK", value: "8" },
              { key: "namespace", value: "knowledge-base" },
              { key: "includeMetadata", value: "true" },
            ],
            variables: [
              { name: "matches", customName: "kb_results", type: "string" },
              {
                name: "matches[0].score",
                customName: "top_score",
                type: "number",
              },
              {
                name: "matches.length",
                customName: "results_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "Synthesize answer from retrieved sources with citations",
        760,
        "You are a knowledge base assistant. Synthesize a comprehensive, accurate answer from the retrieved knowledge base articles. Include specific citations and links. If the sources do not adequately address the query, clearly state what is missing.",
        "User query: {query_text}. Intent: {search_intent}. Retrieved KB articles: {kb_results}. Synthesize JSON with: answer (string, comprehensive response), confidence (number 0-1), sources_used (array of objects with title, url, relevance_score), is_answered (boolean, true if KB adequately addresses the query), gaps (array of strings if anything is missing), related_articles (array of strings with titles of related content).",
        [
          { field: "answer", as: "synthesized_answer", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "sources_used", as: "sources_used", type: "string" },
          { field: "is_answered", as: "is_answered", type: "boolean" },
          { field: "related_articles", as: "related_articles", type: "string" },
        ],
      ),
      n(
        "5",
        "condition",
        "Were relevant sources found and confidence above 0.7?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                field: "answer_confidence",
                operator: "greater_than",
                value: "0.7",
              },
              { field: "is_answered", operator: "equals", value: "true" },
            ],
            logic: "and",
          },
        },
      ),
      n(
        "6",
        "sendMessage",
        "Reply with synthesized answer and source links",
        1240,
        {
          sendMessageDetails: {
            channel: "chat",
            message:
              "{synthesized_answer}\n\nSources:\n{sources_used}\n\nRelated articles you might find useful:\n{related_articles}",
          },
        },
      ),
      slackNode(
        "7",
        "Escalate unanswered queries to content team",
        1240,
        ":mag: *KB Search Bot — Content Gap Identified*\n\nUser: {user_id}\nQuery: {query_text}\nIntent: {search_intent}\nResults found: {results_count}\nTop score: {top_score}\nConfidence: {answer_confidence}\nAnswered: {is_answered}\n\nThis query could not be adequately answered from existing KB content. Consider creating or updating articles to address this topic.",
        480,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
    summary:
      "Intelligent knowledge base search bot: analyzes search intent and key terms, performs semantic search across articles, FAQs, and guides in Pinecone, synthesizes comprehensive answers with citations, replies to users with high-confidence answers, and escalates content gaps to the content team for KB improvement.",
  },

  // ─────────────────────────────────────────────────────────
  // 25. Multi Language Support Bot
  // ─────────────────────────────────────────────────────────
  "Multi Language Support Bot": {
    nodes: [
      trigger("Customer message in any language", "webhook_app", {
        source: "support-channel",
        event: "message.received",
        howItStarts:
          "Fires when a customer sends a support message in any language through any configured channel.",
        setup:
          "Connect all support channels (chat, email, social) to POST messages to this webhook. Set up Pinecone knowledge base with English articles.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Detect language and translate to English",
        280,
        "You are a multilingual support translator. Detect the language of the incoming message and translate it to English for processing. Preserve the original message for reference.",
        "Detect language and translate this customer message: {webhook_payload}. Return JSON with: customer_id (string), customer_name (string if available), original_message (string, the original text), detected_language (string, ISO 639-1 code like en, es, fr, de, ja, zh, pt, ko, ar), detected_language_name (string like Spanish, French, etc.), english_translation (string, accurate translation to English), translation_confidence (number 0-1), contains_slang_or_idioms (boolean).",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "original_message", as: "original_message", type: "string" },
          {
            field: "detected_language",
            as: "detected_language",
            type: "string",
          },
          {
            field: "detected_language_name",
            as: "detected_language_name",
            type: "string",
          },
          {
            field: "english_translation",
            as: "english_translation",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "Classify intent, urgency, and sentiment in English",
        520,
        "You are a support message classifier. Analyze the English translation of the customer's message to classify intent, urgency, and sentiment for routing and response generation.",
        "Classify this translated customer message: {english_translation}. Original language: {detected_language_name}. Return JSON with: intent (one of: question, complaint, feature_request, billing, technical, account, shipping, praise, general), urgency (low/medium/high), sentiment (positive/neutral/negative/frustrated), topic (string, specific topic), requires_agent (boolean, true if complex or sensitive).",
        [
          { field: "intent", as: "message_intent", type: "string" },
          { field: "urgency", as: "message_urgency", type: "string" },
          { field: "sentiment", as: "customer_sentiment", type: "string" },
          { field: "topic", as: "message_topic", type: "string" },
          { field: "requires_agent", as: "requires_agent", type: "boolean" },
        ],
      ),
      n(
        "4",
        "hitApi",
        "Search English knowledge base for solution in Pinecone",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_host}/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{translation_embedding}" },
              { key: "topK", value: "5" },
              { key: "namespace", value: "support-kb" },
              { key: "includeMetadata", value: "true" },
            ],
            variables: [
              { name: "matches", customName: "kb_matches", type: "string" },
              {
                name: "matches[0].score",
                customName: "top_match_score",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "5",
        "Generate English response from KB results",
        1000,
        "You are a customer support specialist. Generate a helpful, empathetic response in English based on the knowledge base results. This will be translated to the customer's language in the next step.",
        "Customer's question (translated): {english_translation}. Intent: {message_intent}. Sentiment: {customer_sentiment}. KB results: {kb_matches}. Generate JSON with: english_response (string, clear and helpful response), confidence (number 0-1), sources (array of article titles used).",
        [
          { field: "english_response", as: "english_response", type: "string" },
          { field: "confidence", as: "response_confidence", type: "number" },
          { field: "sources", as: "response_sources", type: "string" },
        ],
      ),
      aiNode(
        "6",
        "Translate response back to customer's language",
        1240,
        "You are a professional translator. Translate the support response from English to the customer's language. Maintain the same tone, level of formality, and helpfulness. Adapt cultural references and idioms appropriately for the target language.",
        "Translate this English support response to {detected_language_name} ({detected_language}): {english_response}. Customer's original message was: {original_message}. Match the formality level of the customer's original message. Return JSON with: translated_response (string, the response in the customer's language), back_translation (string, English back-translation for QA), formality_level (formal/informal/neutral).",
        [
          {
            field: "translated_response",
            as: "translated_response",
            type: "string",
          },
          { field: "formality_level", as: "formality_level", type: "string" },
        ],
      ),
      n("7", "sendMessage", "Reply in customer's language", 1480, {
        sendMessageDetails: {
          channel: "chat",
          message: "{translated_response}",
        },
      }),
      sheetsNode(
        "8",
        "Log both original and translated transcript",
        1720,
        "MultiLangSupportLog",
        '[["{customer_id}", "{detected_language}", "{detected_language_name}", "{original_message}", "{english_translation}", "{message_intent}", "{customer_sentiment}", "{english_response}", "{translated_response}", "{response_confidence}", "=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Full multilingual support bot: detects customer language, translates to English for processing, classifies intent and sentiment, searches English knowledge base, generates English response, translates back to customer's language with cultural adaptation, replies in native language, and logs complete bilingual transcripts.",
  },

  // ─────────────────────────────────────────────────────────
  // 26. Multi Language Support Chatbot
  // ─────────────────────────────────────────────────────────
  "Multi Language Support Chatbot": {
    nodes: [
      trigger("Chat message from multilingual customer", "webhook_app", {
        source: "chat-platform",
        event: "chat.message",
        howItStarts:
          "Fires when a customer sends a chat message, supporting conversations in any language with localized knowledge base fallback.",
        setup:
          "Connect your chat platform to POST messages to this webhook. Set up Pinecone with separate namespaces for localized KB corpora (e.g., kb-es, kb-fr, kb-de) and the English fallback (kb-en).",
        inputFields: [],
      }),
      aiNode(
        "2",
        "Detect language and check for localized KB corpus availability",
        280,
        "You are a multilingual chat handler. Detect the customer's language and determine if we have a localized knowledge base corpus for that language.",
        "Detect language of this chat message: {webhook_payload}. Return JSON with: customer_id (string), customer_name (string if available), message_text (string), detected_language (string ISO 639-1 code), detected_language_name (string), supported_locales (array of available KB languages: en, es, fr, de, pt, ja), has_localized_kb (boolean, true if detected_language is in supported_locales), kb_namespace (string, either kb-{detected_language} if localized exists, or kb-en for fallback).",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          {
            field: "detected_language",
            as: "detected_language",
            type: "string",
          },
          {
            field: "detected_language_name",
            as: "detected_language_name",
            type: "string",
          },
          {
            field: "has_localized_kb",
            as: "has_localized_kb",
            type: "boolean",
          },
          { field: "kb_namespace", as: "kb_namespace", type: "string" },
        ],
      ),
      n(
        "3",
        "condition",
        "Does a localized KB corpus exist for this language?",
        520,
        {
          conditionDetails: {
            conditions: [
              { field: "has_localized_kb", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      n("4", "hitApi", "Search native-language KB in Pinecone", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_host}/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{message_embedding}" },
            { key: "topK", value: "5" },
            { key: "namespace", value: "{kb_namespace}" },
            { key: "includeMetadata", value: "true" },
          ],
          variables: [
            {
              name: "matches",
              customName: "native_kb_results",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "native_top_score",
              type: "number",
            },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Search English KB and prepare for translation",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_host}/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{english_translation_embedding}" },
              { key: "topK", value: "5" },
              { key: "namespace", value: "kb-en" },
              { key: "includeMetadata", value: "true" },
            ],
            variables: [
              {
                name: "matches",
                customName: "english_kb_results",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "english_top_score",
                type: "number",
              },
            ],
          },
        },
        480,
      ),
      aiNode(
        "6",
        "Generate channel-appropriate response in customer's language",
        1000,
        "You are a multilingual customer support chatbot. Generate a response in the customer's language. If using native-language KB results, respond directly. If using English KB results, translate the answer naturally. Mark translated responses so the customer knows it was originally in English.",
        "Customer message ({detected_language_name}): {message_text}. Native KB results available: {has_localized_kb}. Native results: {native_kb_results}. English results: {english_kb_results}. Target language: {detected_language_name}. Generate JSON with: response_text (string in the customer's language), source_language (string, the language of the KB source used), is_translated (boolean, true if response was translated from English), confidence (number 0-1), sources_used (array of article titles).",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "source_language", as: "source_language", type: "string" },
          { field: "is_translated", as: "is_translated", type: "boolean" },
          { field: "confidence", as: "response_confidence", type: "number" },
          { field: "sources_used", as: "sources_used", type: "string" },
        ],
      ),
      n(
        "7",
        "sendMessage",
        "Reply with translated-fallback marker if applicable",
        1240,
        {
          sendMessageDetails: {
            channel: "chat",
            message: "{response_text}",
          },
        },
      ),
      sheetsNode(
        "8",
        "Log conversation with language metadata",
        1480,
        "MultiLangChatLog",
        '[["{customer_id}", "{detected_language}", "{detected_language_name}", "{message_text}", "{has_localized_kb}", "{source_language}", "{is_translated}", "{response_text}", "{response_confidence}", "{sources_used}", "=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Advanced multilingual chatbot with localized KB support: detects customer language, checks for native-language knowledge base availability, searches localized KB when available or falls back to English KB with translation, generates responses in the customer's language with translated-fallback markers, and logs complete conversation metadata for localization quality analysis.",
  },
};
